<template>
  <div>
    <el-row class="mb-20">
      <el-col :span="24">
        <!-- Filter controls -->
        <div class="filters-container mb-20" v-if="breakpoint !== 'mobile'">
          <el-row :gutter="20">
            <el-col :span="8">
              <el-input
                placeholder="Search by entity name..."
                v-model="searchQuery"
                prefix-icon="el-icon-search"
                clearable
                class="w-100"
              ></el-input>
            </el-col>
            <el-col :span="5">
              <el-select v-model="filters.entityType" placeholder="Entity Type" clearable class="w-100">
                <el-option v-for="type in entityTypes" :key="type" :label="capitalize(type)" :value="type"></el-option>
              </el-select>
            </el-col>
            <el-col :span="5">
              <el-select v-model="filters.approvalStatus" placeholder="Approval Status" clearable class="w-100">
                <el-option label="Approved" value="approved"></el-option>
                <el-option label="Pending" value="pending"></el-option>
                <el-option label="Declined" value="declined"></el-option>
              </el-select>
            </el-col>
            <el-col :span="5">
              <el-select v-model="filters.expired" placeholder="Expiry Status" clearable class="w-100">
                <el-option label="Expired" :value="true"></el-option>
                <el-option label="Active" :value="false"></el-option>
              </el-select>
            </el-col>
          </el-row>
        </div>

        <!-- Sort controls -->
        <div class="sort-container mb-20">
          <el-row>
            <el-col :span="24" class="d-flex justify-content-between align-items-center">
              <!-- View mode toggle -->
              <div  v-if="breakpoint !== 'mobile'">
                <el-radio-group v-model="viewMode" size="small">
                  <el-radio-button label="card">
                    <i class="el-icon-s-grid"></i> Card
                  </el-radio-button>
                  <el-radio-button label="list">
                    <i class="el-icon-s-order"></i> List
                  </el-radio-button>
                </el-radio-group>
              </div>

              <!-- Sort dropdown -->
              <div class="sort-wrapper" v-if="breakpoint !== 'mobile'">
                <span class="sort-label mr-10">Sort by:</span>
                <el-select v-model="sortBy" placeholder="Sort by" size="small" class="sort-select">
                  <el-option label="None" value=""></el-option>
                  <el-option label="Entity Name (A-Z)" value="entity-asc"></el-option>
                  <el-option label="Entity Name (Z-A)" value="entity-desc"></el-option>
                  <el-option label="Registration Date (Newest)" value="reg-desc"></el-option>
                  <el-option label="Registration Date (Oldest)" value="reg-asc"></el-option>
                  <el-option label="Expiry Date (Newest)" value="exp-desc"></el-option>
                  <el-option label="Expiry Date (Oldest)" value="exp-asc"></el-option>
                </el-select>
              </div>
            </el-col>
          </el-row>
        </div>

        <!-- Card-based layout for desktop when in card view mode -->
        <div v-if="viewMode === 'card' && breakpoint !== 'mobile'">
          <!-- Season group header -->
          <div v-for="seasonGroup in registrationsBySeasonDesktop" :key="seasonGroup.season" class="season-group">
            <div class="season-header" @click="toggleSeason(seasonGroup.season)">
              <div class="d-flex justify-content-between align-items-center">
                <div class="season-title">
                  <i :class="`el-icon-arrow-${seasonGroup.isExpanded ? 'down' : 'right'}`"></i>
                  Season: {{ seasonGroup.season }}
                </div>
                <div class="season-count">
                  {{ seasonGroup.registrations.length }} registrations
                </div>
              </div>
            </div>

            <div v-if="seasonGroup.isExpanded" class="season-content card-grid">
              <el-card
                v-for="registration in seasonGroup.registrations"
                :key="registration._id"
                class="registration-card mb-15"

                :data-entity-type="registration.entityType"
                shadow="hover"
              >
                <div slot="header" class="card-header w-100">
                  <div class="d-flex justify-content-between align-items-start w-100">
                    <div class="d-flex flex-column align-items-start w-100">
                      <div class="entity-type-text">{{ capitalize(registration.entityType) }}</div>
                      <div class="entity-name">{{ entityFormatter(registration) }}</div>
                    </div>
                    <div class="d-flex align-items-center">
                      <el-tag :type="getApprovalTagType(registration)" size="mini" effect="plain" class="status-tag">
                        {{ getApprovalStatus(registration) }}
                      </el-tag>
                    </div>
                  </div>
                </div>

                <div class="card-content">
                  <div class="card-body-row">
                    <div class="info-section">
                      <div class="unified-info-grid">
                        <div class="info-col text-center">
                          <div class="info-label"><i class="el-icon-user"></i> Member Type</div>
                          <div class="info-value">{{ capitalize(registration.memberType) }}</div>
                        </div>

                        <div class="info-col text-center">
                          <div class="info-label"><i class="el-icon-date"></i> Registered On</div>
                          <div class="info-value">{{ msDateFormatter(registration.createdAt) }}</div>
                        </div>

                        <div class="info-col text-center">
                          <div class="info-label"><i class="el-icon-time"></i> Expiry</div>
                          <div class="info-value">{{ registration.expiry ? msDateFormatter(registration.expiry) : 'N/A' }}</div>
                        </div>

                        <!-- Show More button with centered arrow icon directly on unified grid border -->
                        <div class="expand-button-container">
                          <el-button
                            type="text"
                            class="expand-button-icon"
                            size="mini"
                            @click="toggleExpand(registration)"
                          >
                            <i :class="`el-icon-arrow-${registration.expanded ? 'up' : 'down'} arrow-icon`"></i>
                          </el-button>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Expanded content -->
                  <transition name="expand">
                    <div v-if="registration.expanded" class="expanded-content">
                      <el-divider content-position="left">Additional Information</el-divider>

                      <!-- Status information -->
                      <div class="info-section">
                        <h4>Status Information</h4>
                        <div class="status-details">
                          <div class="status-item">
                            <span class="status-label">Activity Status:</span>
                            <el-tag size="mini" effect="plain" :type="getType(registration)" class="status-tag">
                              {{ getStatus(registration) }}
                            </el-tag>
                          </div>
                          <div class="status-item" v-if="get(registration, 'regoEntity.isRenewable') && !alreadyRenew(registration) && registration.productType !== 'family'">
                            <span class="status-label">Renewal:</span>
                            <el-tooltip class="item" effect="dark" :content="`If you choose to renew, per the membership setting your membership will be renewed for ${getRenewalDuration(registration)}`" placement="top-start">
                              <el-button type="text" size="mini" @click="makeRenewPayment(registration)" :disabled="value.isLocked || registration.isValidatingRenewal" :loading="registration.isValidatingRenewal">
                                <i class="el-icon-refresh"></i> {{ registration.isValidatingRenewal ? 'Validating...' : 'Renew Now' }}
                              </el-button>
                            </el-tooltip>
                          </div>
                        </div>
                      </div>

                      <!-- Payment details -->
                      <div v-if="registration.payAfter || !isPaid(registration)" class="info-section">
                        <h4>Payment Details</h4>
                        <div v-if="registration.payAfter">
                          <div class="payment-info">
                            <span class="payment-label">Amount Paid:</span>
                            <span class="payment-value">{{ priceFormatter(getAmountPaid(registration)) }}</span>
                          </div>
                          <div class="payment-info" v-if="!registration.payAfterMeta.fullyPaid && !registration.isPaid">
                            <span class="payment-label">Awaiting Payment:</span>
                            <span class="payment-value">{{ priceFormatter(getAmountRemaining(registration)) }}</span>
                          </div>
                        </div>
                        <div v-else-if="!isPaid(registration)">
                          <div class="payment-info">
                            <span class="payment-label">Amount Paid:</span>
                            <span class="payment-value">{{ priceFormatter(getAmountPaid(registration)) }}</span>
                          </div>
                          <div class="payment-info">
                            <span class="payment-label">Awaiting Payment:</span>
                            <span class="payment-value">{{ priceFormatter(getOutstanding(registration)) }}</span>
                          </div>
                        </div>
                      </div>

                      <!-- Product information -->
                      <div v-if="registration.productType !== 'legacy-membership'" class="info-section">
                        <h4>Product Information</h4>
                        <div class="info-detail">
                          <strong>Product Type:</strong> {{productTypeFormatter(registration.productType)}}
                        </div>
                        <div class="info-detail">
                          <strong>Member Type:</strong> {{ capitalize(registration.memberType) }}
                        </div>
                        <div v-if="registration.subProducts && registration.subProducts.length > 0" class="info-detail">
                          <strong>Sub Products:</strong> {{ joinTag(registration.subProducts) }}
                        </div>
                        <div v-if="registration.tags && registration.tags.length > 0" class="info-detail">
                          <strong>Tags:</strong> {{ joinTag(registration.tags) }}
                        </div>
                      </div>

                      <!-- Additional questions -->
                      <div v-if="registration.additionalQs && registration.additionalQs.length !== 0" class="info-section">
                        <h4>Additional Questions</h4>
                        <div
                          v-for="(value, name) in registration.meta"
                          :key="name"
                          v-show="isShowableAnswer(name, registration.additionalQs)"
                          class="info-detail"
                        >
                          <strong>{{ getQuestion(name, registration.additionalQs) | startCase }}:</strong> {{ metaFormat(value) }}
                        </div>
                      </div>

                      <!-- Files section -->
                      <div class="info-section">
                        <h4>Files Uploaded</h4>
                        <div v-for="([group, files]) in groupedUploads(registration.files)" :key="group" class="file-group">
                          <h5>{{ group }}</h5>
                          <el-upload
                            :auto-upload="false"
                            :ref="`uploadDesktop-${registration._id}-${group}`"
                            :file-list="files ? files.map(file => ({_id: file._id, name: `${file.originalName || file.fileName} Uploaded At: ${dateFormatter(file.createdAt)}`, url: file.path, registrationId: file.meta.registrationId, type: file.fileType})) : []"
                            :on-preview="previewUpload"
                            :on-change="(file, fileList) => handleChange(file, fileList, {
                              _id: files._id,
                              registrationId: registration._id,
                              additionalQuestion: group,
                              entityId: registration.entityId,
                              entityType: registration.entityType,
                              memberId: value._id,
                              type: 'additional-question',
                            })"
                            :on-error="onError"
                            :before-upload="beforeUpload"
                            :before-remove="removeFile"
                            action=""
                            :headers="uploadHeaders"
                            :accept="accept"
                            :disabled="getUploadExpiry(registration)"
                          >
                            <el-button
                              slot="trigger"
                              size="small"
                              type="primary"
                              v-if="isNotExpired(registration)"
                            >
                              Add File(s)
                            </el-button>
                          </el-upload>
                        </div>
                      </div>
                    </div>
                  </transition>

                  <!-- Action section with buttons only -->
                  <div class="action-section d-flex justify-content-end align-items-center">
                    <!-- Payment buttons first -->
                    <div class="payment-action-buttons" v-if="!isPaid(registration) || (get(registration, 'regoEntity.isRenewable') && registration.productType !== 'family' && !alreadyRenew(registration))">
                      <el-button
                        v-if="!isPaid(registration)"
                        type="danger"
                        size="mini"
                        class="pay-button"
                        @click="makePayment(registration)"
                      >
                        Pay ${{ getOutstanding(registration) / 100 }}
                      </el-button>

                      <el-button
                        v-if="get(registration, 'regoEntity.isRenewable') && registration.productType !== 'family' && !alreadyRenew(registration)"
                        type="warning"
                        size="mini"
                        class="renew-button"
                        @click="makeRenewPayment(registration)"
                        :disabled="value.isLocked"
                      >
                        Renew
                      </el-button>
                    </div>

                    <!-- Action buttons without grouping -->
                    <div class="action-buttons">
                      <el-tooltip content="Download Receipt" placement="top">
                        <el-button type="primary"  size="mini" icon="el-icon-download" @click="downloadReceipt(registration)"></el-button>
                      </el-tooltip>

                      <el-tooltip content="Update Questions" placement="top">
                        <el-button type="primary"  size="mini" icon="el-icon-edit-outline" @click="showAQDialog(registration)"></el-button>
                      </el-tooltip>

                      <el-tooltip content="Update Saleable Items" placement="top">
                        <el-button type="warning"  size="mini" icon="el-icon-edit" @click="showAQDialogSI(registration)"></el-button>
                      </el-tooltip>

                      <template v-if="registration.entityType === 'membership'">
                        <el-dropdown trigger="click" size="mini">
                          <el-button type="primary"  size="mini" icon="el-icon-postcard"></el-button>
                          <el-dropdown-menu slot="dropdown">
                            <el-dropdown-item @click.native="downloadMSCard({value: registration, type:'pdf'})">Download as PDF</el-dropdown-item>
                            <el-dropdown-item @click.native="downloadMSCard({value: registration, type:'png'})">Download as PNG</el-dropdown-item>
                            <el-dropdown-item @click.native="downloadMSCard({value: registration, type:'regenerate'})">Regenerate Card</el-dropdown-item>
                          </el-dropdown-menu>
                        </el-dropdown>

                        <el-tooltip content="View QR Code" placement="top">
                          <el-button type="primary"  size="mini" icon="el-icon-view" @click="showQRCode(registration)"></el-button>
                        </el-tooltip>
                      </template>

                      <el-tooltip content="Request Deregistration" placement="top">
                        <el-button
                          type="danger"

                          size="mini"
                          icon="el-icon-delete"
                          :disabled="registration.deregistered || isExpired(registration) || deregistrationDisabled || get(registration, 'deregistrationRequest[0].status') === 'pending' || get(registration, 'deregistrationRequest[0].status') === 'success'"
                          @click="deregisterRequest(registration)"
                        ></el-button>
                      </el-tooltip>
                    </div>
                  </div>

                  <!-- Show other buttons only when needed -->
                  <div class="secondary-actions d-flex justify-content-end" v-if="isUpgradeEligible(registration)">
                    <template v-if="isUpgradeEligible(registration)">
                      <el-button type="primary" size="mini" @click="handleDropdown(registration,'open')" :disabled="value.isLocked" class="ml-5">
                        Upgrade
                      </el-button>
                    </template>
                  </div>

                  <!-- Upgrade dropdown section -->
                  <div v-if="registration.entityType === 'membership' && get(registration, 'regoEntity.products.isUpgradeable') && registration.upgradingDropdown" class="upgrade-dropdown mt-10">
                    <el-button class="mb-05" size="mini" type="danger" @click="handleDropdown(registration, 'close')">
                      Cancel
                    </el-button>
                    <el-cascader
                      v-model="registration.regoEntity.membershipToUpgrade"
                      :options="getCascaderOptionsForRegistration(registration)"
                      :props="{label: 'name', value: '_id', children: 'products' }"
                      :key="`card-${registration._id}-${registration.upgradingTimestamp || 0}-${key}`"
                      clearable
                      filterable
                      size="small"
                      :show-all-levels="true"
                      separator=" -> "
                      @change="handleChangeMem(registration)"
                      class="w-100"
                    >
                      <template slot-scope="{ data }">
                        <div style="display: flex; justify-content: space-between; align-items: center; width: 100%">
                          <span v-if="data.products">{{ data.name }}</span>
                          <template v-else>
                            <span>{{ getProductType(data.type) }}</span>
                            <el-tag size="mini" style="margin-left: 5px">{{ capitalize(data.memberType) }}</el-tag>
                          </template>
                        </div>
                      </template>
                    </el-cascader>
                  </div>
                </div>
              </el-card>
            </div>
          </div>
        </div>

        <!-- List-based layout for desktop when in list view mode -->
        <div v-if="viewMode === 'list' && breakpoint !== 'mobile'" class="list-view">
          <div v-for="seasonGroup in registrationsBySeasonDesktop" :key="seasonGroup.season" class="season-group">
            <!-- Season header just like in card view -->
            <div class="season-header" @click="toggleSeason(seasonGroup.season)">
              <div class="d-flex justify-content-between align-items-center">
                <div class="season-title">
                  <i :class="`el-icon-arrow-${seasonGroup.isExpanded ? 'down' : 'right'}`"></i>
                  Season: {{ seasonGroup.season }}
                </div>
                <div class="season-count">
                  {{ seasonGroup.registrations.length }} registrations
                </div>
              </div>
            </div>

            <!-- List content -->
            <div v-if="seasonGroup.isExpanded" class="list-content">
              <el-table
                :data="seasonGroup.registrations"
                style="width: 100%"
                row-key="_id"
                :row-class-name="getRowClass"
                border
                :cell-style="{textAlign:'center'}"
                :header-row-style="{height: '48px'}"
                :row-style="{height: '48px'}"
                class="clean-table"
              >
                <!-- Expand column as first column -->
                <el-table-column type="expand" width="60">
                  <template slot-scope="scope">
                    <div class="table-expand-content">
                      <el-tabs type="border-card" class="expanded-tabs">
                        <el-tab-pane label="Details">
                          <div class="expanded-details">
                            <!-- Status information -->
                            <div class="info-section">
                              <h4>Status Information</h4>
                              <div class="status-details">
                                <div class="status-item">
                                  <span class="status-label">Activity Status:</span>
                                  <el-tag size="mini" effect="plain" :type="getType(scope.row)" class="status-tag">
                                    {{ getStatus(scope.row) }}
                                  </el-tag>
                                </div>
                                <div class="status-item" v-if="get(scope.row, 'regoEntity.isRenewable') && !alreadyRenew(scope.row) && scope.row.productType !== 'family'">
                                  <span class="status-label">Renewal:</span>
                                  <el-tooltip class="item" effect="dark" :content="`If you choose to renew, per the membership setting your membership will be renewed for ${getRenewalDuration(scope.row)}`" placement="top-start">
                                    <el-button type="text" size="mini" @click="makeRenewPayment(scope.row)" :disabled="value.isLocked">
                                      <i class="el-icon-refresh"></i> Renew Now
                                    </el-button>
                                  </el-tooltip>
                                </div>
                              </div>
                            </div>

                            <!-- Payment details -->
                            <div v-if="scope.row.payAfter || !isPaid(scope.row)" class="info-section">
                              <h4>Payment Details</h4>
                              <div v-if="scope.row.payAfter">
                                <div class="payment-info">
                                  <span class="payment-label">Amount Paid:</span>
                                  <span class="payment-value">{{ priceFormatter(getAmountPaid(scope.row)) }}</span>
                                </div>
                                <div class="payment-info" v-if="!scope.row.payAfterMeta.fullyPaid && !scope.row.isPaid">
                                  <span class="payment-label">Awaiting Payment:</span>
                                  <span class="payment-value">{{ priceFormatter(getAmountRemaining(scope.row)) }}</span>
                                </div>
                              </div>
                            </div>

                            <!-- Product information -->
                            <div v-if="scope.row.productType !== 'legacy-membership'" class="info-section">
                              <h4>Product Information</h4>
                              <div class="info-detail">
                                <strong>Product Type:</strong> {{productTypeFormatter(scope.row.productType)}}
                              </div>
                              <div class="info-detail">
                                <strong>Member Type:</strong> {{ capitalize(scope.row.memberType) }}
                              </div>
                              <div v-if="scope.row.subProducts && scope.row.subProducts.length > 0" class="info-detail">
                                <strong>Sub Products:</strong> {{ joinTag(scope.row.subProducts) }}
                              </div>
                              <div v-if="scope.row.tags && scope.row.tags.length > 0" class="info-detail">
                                <strong>Tags:</strong> {{ joinTag(scope.row.tags) }}
                              </div>
                            </div>

                            <!-- Additional questions -->
                            <div v-if="scope.row.additionalQs && scope.row.additionalQs.length !== 0" class="info-section">
                              <h4>Additional Questions</h4>
                              <div
                                v-for="(value, name) in scope.row.meta"
                                :key="name"
                                v-show="isShowableAnswer(name, scope.row.additionalQs)"
                                class="info-detail"
                              >
                                <strong>{{ getQuestion(name, scope.row.additionalQs) | startCase }}:</strong> {{ metaFormat(value) }}
                              </div>
                            </div>

                            <!-- Results section -->
                            <div v-if="Object.keys(scope.row.results).length" class="info-section">
                              <h4>Results</h4>
                              <div class="info-detail">
                                <strong>{{ scope.row.results.name }}:</strong> {{ scope.row.results.value }} ({{ getResultLabel(scope.row.results.type) }})
                              </div>
                            </div>
                          </div>
                        </el-tab-pane>

                        <el-tab-pane label="Files" v-if="scope.row.files && scope.row.files.length > 0">
                          <div class="file-list">
                            <div v-for="([group, files]) in groupedUploads(scope.row.files)" :key="group" class="file-group">
                              <h5>{{ group }}</h5>
                              <el-upload
                                :auto-upload="false"
                                :ref="`uploadList-${scope.row._id}-${group}`"
                                :file-list="files ? files.map(file => ({_id: file._id, name: `${file.originalName || file.fileName} Uploaded At: ${dateFormatter(file.createdAt)}`, url: file.path, registrationId: file.meta.registrationId, type: file.fileType})) : []"
                                :on-preview="previewUpload"
                                :on-change="(file, fileList) => handleChange(file, fileList, {
                                  _id: files._id,
                                  registrationId: scope.row._id,
                                  additionalQuestion: group,
                                  entityId: scope.row.entityId,
                                  entityType: scope.row.entityType,
                                  memberId: value._id,
                                  type: 'additional-question',
                                })"
                                :on-error="onError"
                                :before-upload="beforeUpload"
                                :before-remove="removeFile"
                                action=""
                                :headers="uploadHeaders"
                                :accept="accept"
                                :disabled="getUploadExpiry(scope.row)"
                              >
                                <el-button
                                  slot="trigger"
                                  size="small"
                                  type="primary"
                                  v-if="isNotExpired(scope.row)"
                                >
                                  Add File(s)
                                </el-button>
                              </el-upload>
                            </div>
                          </div>
                        </el-tab-pane>
                      </el-tabs>

                      <!-- Action section for payment buttons -->
                      <div class="expanded-actions" v-if="!isPaid(scope.row) || (get(scope.row, 'regoEntity.isRenewable') && scope.row.productType !== 'family' && !alreadyRenew(scope.row))">
                        <el-button
                          v-if="!isPaid(scope.row)"
                          type="danger"
                          size="small"
                          @click="makePayment(scope.row)"
                        >
                          Pay ${{ getOutstanding(scope.row) / 100 }}
                        </el-button>

                        <el-button
                          v-if="get(scope.row, 'regoEntity.isRenewable') && scope.row.productType !== 'family' && !alreadyRenew(scope.row)"
                          type="warning"
                          size="small"
                          @click="makeRenewPayment(scope.row)"
                          :disabled="value.isLocked"
                        >
                          Renew
                        </el-button>
                      </div>

                    </div>
                  </template>
                </el-table-column>

                <!-- Entity Type Column - moved up in the order -->
                <el-table-column label="Type" width="120" align="center">
                  <template slot-scope="scope">
                    <span class="list-value entity-value">{{ capitalize(scope.row.entityType) }}</span>
                  </template>
                </el-table-column>

                <!-- Entity Name Column with tooltip -->
                <el-table-column prop="entityName" label="Entity" min-width="170" align="left">
                  <template slot-scope="scope">
                    <div class="entity-cell">
                      <div class="entity-display">
                        <el-tooltip :content="entityFormatter(scope.row)" placement="top">
                          <span class="entity-name-value">{{ entityFormatter(scope.row) }}</span>
                        </el-tooltip>
                        <div v-if="scope.row.team && scope.row.team.teamCode" class="team-code-row">
                          <span class="team-code-value">Team Code: {{ scope.row.team.teamCode }}</span>
                        </div>
                      </div>
                      <div class="payment-actions-container" v-if="!isPaid(scope.row) || (get(scope.row, 'regoEntity.isRenewable') && scope.row.productType !== 'family' && !alreadyRenew(scope.row))">
                        <el-button
                          v-if="!isPaid(scope.row)"
                          type="danger"
                          size="mini"
                          @click="makePayment(scope.row)"
                          class="pay-button-list"
                        >
                          Pay ${{ getOutstanding(scope.row) / 100 }}
                        </el-button>
                        <el-button
                          v-else-if="get(scope.row, 'regoEntity.isRenewable') && scope.row.productType !== 'family' && !alreadyRenew(scope.row)"
                          type="warning"
                          size="mini"
                          @click="makeRenewPayment(scope.row)"
                          :disabled="value.isLocked"
                          class="renew-button-list"
                        >
                          Renew
                        </el-button>
                      </div>
                    </div>
                  </template>
                </el-table-column>

                <!-- Member Type Column -->
                <el-table-column label="Member" width="110" align="center">
                  <template slot-scope="scope">
                    <span class="list-value break-word">{{ capitalize(scope.row.memberType) }}</span>
                  </template>
                </el-table-column>

                <!-- Registration Date Column -->
                <el-table-column label="Registered" width="105" align="center">
                  <template slot-scope="scope">
                    <span class="list-value date-value">{{ msDateFormatter(scope.row.createdAt) }}</span>
                  </template>
                </el-table-column>

                <!-- Expiry Column -->
                <el-table-column label="Expiry" width="105" align="center">
                  <template slot-scope="scope">
                    <span class="list-value date-value">{{ scope.row.expiry ? msDateFormatter(scope.row.expiry) : 'N/A' }}</span>
                  </template>
                </el-table-column>

                <!-- Status Column -->
                <el-table-column label="Status" width="120" align="center">
                  <template slot-scope="scope">
                    <el-tag :type="getApprovalTagType(scope.row)" size="mini" effect="plain" class="break-word">
                      {{ getApprovalStatus(scope.row) }}
                    </el-tag>
                  </template>
                </el-table-column>

                <!-- Actions Column -->
                <el-table-column width="60" label="" class-name="actions-column">
                  <template slot-scope="scope">
                    <div class="list-actions">
                      <el-dropdown trigger="click" size="small">
                        <i class="el-icon-more action-icon"></i>
                        <el-dropdown-menu slot="dropdown">
                          <el-dropdown-item divided @click.native="downloadReceipt(scope.row)">
                            <i class="el-icon-download"></i> Download Receipt
                          </el-dropdown-item>
                          <el-dropdown-item @click.native="showAQDialog(scope.row)">
                            <i class="el-icon-edit-outline"></i> Update Questions
                          </el-dropdown-item>
                          <el-dropdown-item @click.native="showAQDialogSI(scope.row)">
                            <i class="el-icon-edit"></i> Update Saleable Items
                          </el-dropdown-item>
                          <el-dropdown-item v-if="isUpgradeEligible(scope.row)" @click.native="showUpgradeDialogHandler(scope.row)">
                            <i class="el-icon-upload2"></i> Upgrade Membership
                          </el-dropdown-item>
                          <template v-if="scope.row.entityType === 'membership' && (!scope.row.approvalStatus || (scope.row.approvalStatus !== 'declined' && scope.row.approvalStatus !== 'pending'))">
                            <el-dropdown-item divided @click.native="downloadMSCard({value: scope.row, type:'pdf'})">
                              <i class="el-icon-postcard"></i> Download PDF Card
                            </el-dropdown-item>
                            <el-dropdown-item @click.native="downloadMSCard({value: scope.row, type:'png'})">
                              <i class="el-icon-postcard"></i> Download PNG Card
                            </el-dropdown-item>
                            <el-dropdown-item @click.native="downloadMSCard({value: scope.row, type:'regenerate'})">
                              <i class="el-icon-refresh"></i> Regenerate Card
                            </el-dropdown-item>
                            <el-dropdown-item @click.native="showQRCode(scope.row)">
                              <i class="el-icon-view"></i> View QR Code
                            </el-dropdown-item>
                          </template>
                          <el-dropdown-item divided @click.native="deregisterRequest(scope.row)" :disabled="scope.row.deregistered || isExpired(scope.row) || deregistrationDisabled || get(scope.row, 'deregistrationRequest[0].status') === 'pending' || get(scope.row, 'deregistrationRequest[0].status') === 'success'">
                            <i class="el-icon-delete"></i> Request Deregistration
                          </el-dropdown-item>
                        </el-dropdown-menu>
                      </el-dropdown>
                    </div>
                  </template>
                </el-table-column>
              </el-table>
            </div>
          </div>
        </div>

        <!-- Mobile view -->
        <div v-if="breakpoint === 'mobile'">
          <!-- Keep the existing mobile code here -->
          <div class="filters-container mb-10">
            <el-row :gutter="10">
              <el-col :span="12">
                <el-select v-model="mobileFilters.entityType" placeholder="Entity Type" clearable class="w-100">
                  <el-option v-for="type in entityTypes" :key="type" :label="capitalize(type)" :value="type"></el-option>
                </el-select>
              </el-col>
              <el-col :span="12">
                <el-select v-model="mobileFilters.expired" placeholder="Expiry Status" clearable class="w-100">
                  <el-option label="Expired" :value="true"></el-option>
                  <el-option label="Active" :value="false"></el-option>
                </el-select>
              </el-col>
            </el-row>
          </div>
          <div v-for="registration in filteredMobileRegistrations" :key="registration.id" class="mb-4" >
            <el-card class="box-card mb-10 br-05 highlight-card" shadow="always" >
              <div slot="header" class="header-container">
  <div class="d-flex justify-content-between align-items-start">
    <div class="entity-info-mobile">
      <span class="entity-name">{{ entityFormatter(registration) }}</span>
      <div v-if="registration.team && registration.team.teamCode" class="team-code-mobile">
        <span class="team-code-mobile-value" v-if="registration.team?.teamCode">Team Code: {{ registration.team.teamCode }}</span>
      </div>
    </div>
    <div class="actions-container d-flex align-items-center gap-10">

      <el-dropdown class="more-actions-dropdown" trigger="click">
        <i class="el-icon-more"></i>
        <el-dropdown-menu slot="dropdown">
          <el-dropdown-item icon="el-icon-download" @click.native="downloadReceipt(registration)">
            Download Receipt
          </el-dropdown-item>
          <el-dropdown-item icon="el-icon-edit-outline" @click.native="showAQDialog(registration)">
            Update Additional Question
          </el-dropdown-item>
          <el-dropdown-item
            v-if="registration.entityType === 'membership' && (!registration.approvalStatus || (registration.approvalStatus !== 'declined' && registration.approvalStatus !== 'pending'))"
            icon="el-icon-postcard"
            @click.native="downloadMSCard({value: registration, type: 'pdf'})"
          >
            Download Membership Card as PDF
          </el-dropdown-item>
          <el-dropdown-item
            v-if="registration.entityType === 'membership' && (!registration.approvalStatus || (registration.approvalStatus !== 'declined' && registration.approvalStatus !== 'pending'))"
            icon="el-icon-postcard"
            @click.native="downloadMSCard({value: registration, type: 'png'})"
          >
            Download Membership Card as PNG
          </el-dropdown-item>
          <el-dropdown-item
            v-if="registration.entityType === 'membership' && (!registration.approvalStatus || (registration.approvalStatus !== 'declined' && registration.approvalStatus !== 'pending'))"
            icon="el-icon-full-screen"
            @click.native="showQRCode(registration)"
          >
            View QR Code
          </el-dropdown-item>
          <el-dropdown-item
            v-if="registration.entityType === 'membership' && (!registration.approvalStatus || (registration.approvalStatus !== 'declined' && registration.approvalStatus !== 'pending'))"
            icon="el-icon-refresh"
            @click.native="downloadMSCard({value: registration, type: 'regenerate'})"
          >
            Regenerate Card
          </el-dropdown-item>
          <el-dropdown-item :disabled="registration.deregistered || isExpired(registration) || deregistrationDisabled || get(registration, 'deregistrationRequest[0].status') === 'pending' || get(registration, 'deregistrationRequest[0].status') === 'success'" icon="el-icon-delete" @click.native="deregisterRequest(registration)">
            Request Deregistration
          </el-dropdown-item>
        </el-dropdown-menu>
      </el-dropdown>
    </div>
  </div>
</div>
<div class="upgrade-section mb-10" v-if="isUpgradeEligible(registration) || registration.upgradingDropdown">
    <el-button
      size="small"
      v-if="isUpgradeEligible(registration)"
      type="primary"
      :disabled="value.isLocked"
      @click="handleDropdown(registration,'open')"
      :key="key"
    >
      Upgrade Membership
    </el-button>

    <div v-if="registration.entityType === 'membership' && get(registration, 'regoEntity.products.isUpgradeable') && registration.upgradingDropdown">
      <el-button
        class="mb-05"
        size="small"
        type="danger"
        @click="handleDropdown(registration, 'close')"
        :key="key"
      >
        Cancel
      </el-button>

      <el-cascader
        v-model="registration.regoEntity.membershipToUpgrade"
        :options="getCascaderOptionsForRegistration(registration)"
        :props="{label: 'name', value: '_id', children: 'products' }"
        :key="`mobile-${registration._id}-${registration.upgradingTimestamp || 0}-${key}`"
        clearable
        filterable
        size="medium"
        :show-all-levels="true"
        separator=" -> "
        @change="handleChangeMem(registration)"
        class="w-100"
      />
    </div>
  </div>
              <div class="d-grid grid-cols-2 ">
                <div class="grid-item">
                  <div class="mb-05">
                    <!-- entity type -->
                    <!-- <i class="el-icon-s-custom"></i> -->
                    <span class="entity-type-mobile">
                      {{ registration.entityType | startCase}}
                    </span>
                  </div>
                  <div class="mb-05">
                    <i class="el-icon-user"></i>
                    Type: {{ registration.memberType | startCase }}
                  </div>
                  <div class="">
                    <i class="el-icon-date"></i>
                    Reg. :  {{ msDateFormatter(registration.createdAt) }}
                  </div>
                </div>
                <div class="grid-item text-right" >
                  <div class="mb-05" v-if="registration.approvalStatus === 'approved'">
                    <!-- status tag -->
                    <el-tag v-if="!get(registration, 'regoEntity.isRenewable')" effect="plain" :type="getType(registration)" size="mini" class="status-tag" @click="handleStatusClicked(registration)">{{ getStatus(registration) }}</el-tag>
                    <el-popconfirm
                      v-else
                      confirm-button-text='Proceed'
                      cancel-button-text='No, Thanks'
                      icon="el-icon-question"
                      title="Do you want to renew this membership for 12 months?"
                      :disabled="value.isLocked"
                      @confirm="handleStatusClicked(registration)"
                    >
                      <el-tag slot="reference" :type="getType(registration)" size="mini" effect="plain" class="status-tag">{{ getStatus(registration) }}</el-tag>
                    </el-popconfirm>
                  </div>
                  <div v-else>
                <div  class="mb-05">
                  <el-tag size="mini" effect="plain" type="warning" class="status-tag">{{ getApprovalStatus(registration)}}</el-tag>
                  <el-tooltip
                    class="item"
                    effect="dark"
                    content="This membership / event is not approved unless the status of this registration has been adjusted to 'Approved'"
                    placement="top"
                    v-if="getApprovalStatus(registration) === 'Pending'"
                  >
                    <i class="el-icon-info" style="margin-left: 5px;"></i>
                  </el-tooltip>
                </div>
                </div>
                  <div class="grid-item mb-05">
                    <i class="el-icon-s-order"></i>
                    Season: {{ registration.season }}
                  </div>

                  <div class="grid-item">
                    <i class="el-icon-date"></i>
                    Exp. :  {{ msDateFormatter(registration.expiry) }}
                  </div>
                </div>
              </div>
              <transition
              @before-enter="beforeEnter"
              @enter="enter"
              @before-leave="beforeLeave"
              @leave="leave"
              name="expand">
              <div v-if="registration.expanded" class="expandable-content mt-10">
                <div v-if="registration.additionalQs && registration.additionalQs.length !== 0" class="info-section">
                  <h5>Additional Questions</h5>
                  <p v-for="(value, name) in registration.meta" :key="name" v-show="isShowableAnswer(name, registration.additionalQs)" class="info-detail">
                    <strong>{{ getQuestion(name, registration.additionalQs) | startCase }}:</strong> {{ metaFormat(value) }}
                  </p>
                </div>
              <div v-if="registration.productType !== 'legacy-membership'">
                Product Type: {{ productTypeFormatter(registration.productType) }}
              </div>
              <div class="mt-05" v-if="registration.subProducts && registration.subProducts.length > 0">Sub Products: {{ joinTag(registration.subProducts) }}</div>
              <div v-if="registration.tags && registration.tags.length > 0">Tags: {{ joinTag(registration.tags) }}</div>
              <!-- <div>Status: {{ registration.expired ? 'Inactive' : 'Active' }}</div> -->
              <div class=" mt-05">
                <span>Result: </span>
                <div v-if="Object.keys(registration.results).length">
                  <div>{{ registration.results.name }}</div>
                  <div class="font-light">{{ registration.results.value }} ({{ getResultLabel(registration.results.type) }})</div>
                </div>
                <span v-else>Not Released Yet</span>
              </div>
              <div class="mt-05" v-if="registration.payAfter">
                <div class="mb-10">Amount Paid: {{ priceFormatter(getAmountPaid(registration)) }}</div>
                <div class="mb-10" v-if="!registration.payAfterMeta.fullyPaid && !registration.isPaid">Awaiting Payment: {{ priceFormatter(getAmountRemaining(registration)) }}</div>
                <el-button class="mb-10" v-if="!registration.payAfterMeta.fullyPaid && !registration.isPaid" size="small" type="primary"  @click="makePayment(registration)">
                  Pay Now
                </el-button>
              </div>
              <div v-if="get(registration, 'regoEntity.isRenewable') && registration.productType !== 'family' && !alreadyRenew(registration)" class="mb-10">
  <div class="renew">
    <span>
      Renewal
      <el-tooltip class="item" effect="dark" :content="`If you choose to renew, per the membership setting your membership will be renewed for ${getRenewalDuration(registration)}`" placement="top-start">
        <i class="el-icon-info info"/>
      </el-tooltip>
    </span>
  </div>
  <el-button size="small" type="primary"  @click="makeRenewPayment(registration)" :disabled="value.isLocked || registration.isValidatingRenewal" :loading="registration.isValidatingRenewal">
    {{ registration.isValidatingRenewal ? 'Validating...' : 'Renew Now' }}
  </el-button>
</div>
              <!-- <div v-if="get(registration, 'regoEntity.isRenewable')" class="mb-10">
                <div class="renew">
                  Renewal
                  <el-tooltip class="item" effect="dark" content="If you choose to renew 12 months will be added to the registration end date of the membership" placement="top-start">
                    <i class="el-icon-info info"></i>
                  </el-tooltip>
                </div>
                <el-button size="small" type="primary" @click="makeRenewPayment(registration)">
                  Renew Now
                </el-button>
              </div> -->
              <div>
                    <div>Files Uploaded: </div>
                    <div v-for="([group, files]) in groupedUploads(registration.files)" :key="group">
                      <h5 class="mt-0 mb-0">{{ group }}</h5>
                      <el-upload
                        :auto-upload="false"
                        :ref="`uploadDesktop-${registration._id}-${group}`"
                        :file-list="files ? files.map(file => ({_id: file._id, name: `${file.originalName || file.fileName} Uploaded At: ${dateFormatter(file.createdAt)}`, url: file.path, registrationId: file.meta.registrationId, type: file.fileType})) : []"
                        :on-preview="previewUpload"
                        :on-change="(file, fileList) => handleChange(file, fileList, {
                          _id: files._id,
                          registrationId: registration._id,
                          additionalQuestion: group,
                          entityId: registration.entityId,
                          entityType: registration.entityType,
                          memberId: value._id,
                          type: 'additional-question',
                        })"
                        :on-error="onError"
                        :before-upload="beforeUpload"
                        :before-remove="removeFile"
                        action=""
                        :headers="uploadHeaders"
                        :accept="accept"
                        :disabled="getUploadExpiry(registration)"

                        >
                        <el-button
                        slot="trigger"
                        size="small"
                        type="primary"

                        v-if= "isNotExpired(registration)"
                        >
                          Add File(s)
                        </el-button>
                        <!-- <div class="el-upload__tip" slot="tip">{{ getUploadTip(scope.row, group) }}</div> -->
                      </el-upload>
                    </div>
                  </div>
                  </div>
                  </transition>
                  <!-- Arrow icon at the bottom -->
                  <div class="expand-arrow" @click="toggleExpand(registration)">
                    <i :class="`el-icon-arrow-${registration.expanded ? 'up' : 'down'}`"></i>
                  </div>
            </el-card>
          </div>
        </div>
      </el-col>
    </el-row>
    <additional-qs-dialog v-if="showQsDialog" @updateFiles="updateFiles" @updateMeta="updateMeta" @closeDialog="closeDialog" v-model="latestDialogValue" :registration="latestDialogValue.registration" />
    <additional-qs-dialog-saleable-item
      v-if="showQsDialogSI"
      @closeDialog="closeDialogSI"
      v-model="latestDialogValueSI"
      :registration="latestDialogValueSI.registration"
    />
    <el-dialog
      :title="`${get(selectedRego, 'name')}`"
      :visible.sync="familyMSDialog"
      width="30%"
      >
      <div style=" overflow-x: auto;">
        <el-table
          style="width: 100%"
          :data="familyMSList">
          <el-table-column
            prop="gender"
            label="Gender"
            width="100">
          </el-table-column>
          <el-table-column
            label="Member Type"
            width="120">
            <template v-slot="scope">
              {{ scope.row.memberType | cap }}
            </template>
          </el-table-column>
          <el-table-column
            prop="ageFrom"
            label="Age From"
            width="100">
          </el-table-column>
          <el-table-column
            prop="ageTo"
            label="Age To"
            width="100">
          </el-table-column>
          <el-table-column width="200" label="Member">
            <template v-slot="scope">
              <el-select v-model="scope.row.memberId" placeholder="Select Member">
                <el-option v-for="e in scope.row.availableMembers" :label="`${e.profile.firstName} ${e.profile.lastName}`" :key="e._id" :value="e._id" />
              </el-select>
            </template>
          </el-table-column>
        </el-table>
        <el-row>
          <div class="member-btn d-flex justify-content-center flex-0 mt-15">
            <el-button @click="cancelFamilyRego" >Cancel</el-button>
            <el-button :disabled="!selectedMember" type="primary"  class="color-highlight bd-0" @click="initRegoFamily(familyMSList)">Register</el-button>
          </div>
        </el-row>
      </div>
    </el-dialog>
    <renew-payment
  v-if="showRenewPayment"
  @closeDialog="closeDialog"
  v-model="latestDialogValue"
  :rego="latestDialogValue.registration"
/>
    <!-- el dialog to display qrcode -->
    <el-dialog
      :title="`Qr Code for membership card`"
      :visible.sync="showQrCodeDialog"
      width="10%"
      >
      <div style=" overflow-x: auto; width:100%">
        <div class="d-flex justify-content-center">
          <img :src="qrCode" alt="QR Code" />
        </div>
      </div>
    </el-dialog>

    <!-- Upgrade Membership Dialog -->
    <el-dialog
      :title="`Upgrade Membership - ${get(upgradeDialogData, 'registration.entityName', '')}`"
      :visible.sync="showUpgradeDialog"
      width="60%"
      class="upgrade-membership-dialog"
      :close-on-click-modal="false"
      @close="closeUpgradeDialog"
    >
      <div class="upgrade-dialog-content">
        <!-- Loading State -->
        <div v-if="upgradeDialogState === 'loading'" class="upgrade-loading">
          <i class="el-icon-loading"></i>
          <span>Loading upgrade options...</span>
        </div>

        <!-- Empty State -->
        <div v-else-if="upgradeDialogState === 'empty'" class="upgrade-empty-state">
          <i class="el-icon-box"></i>
          <div class="empty-title">No Upgrade Options Available</div>
          <div class="empty-description">
            There are no membership upgrade options available for this registration at the moment.
          </div>
        </div>

        <!-- Ready State -->
        <div v-else-if="upgradeDialogState === 'ready'">
          <p class="upgrade-instruction">
            Select the membership you want to upgrade to:
          </p>

          <div class="upgrade-cascader">
            <el-cascader
              v-model="upgradeDialogData.selectedUpgrade"
              :options="getCascaderOptionsForRegistration(upgradeDialogData.registration)"
              :props="{label: 'name', value: '_id', children: 'products'}"
              :key="`upgrade-dialog-${upgradeDialogData.registration._id || 0}-${Date.now()}`"
              clearable
              filterable
              placeholder="Choose membership upgrade option"
              :show-all-levels="false"
              separator=" → "
              size="large"
              popper-class="upgrade-cascader-popper"
            >
              <template slot-scope="{ data }">
                <div class="membership-option-content">
                  <span v-if="data.products" class="membership-name">{{ data.name }}</span>
                  <template v-else>
                    <div class="membership-details">
                      <span class="membership-type">{{ getProductType(data.type) }}</span>
                      <el-tag size="mini" type="info">{{ capitalize(data.memberType) }}</el-tag>
                    </div>
                  </template>
                </div>
              </template>
            </el-cascader>
          </div>

          <div v-if="upgradeDialogData.selectedUpgrade" class="mt-20">
            <el-alert
              title="Upgrade Confirmation"
              description="Please review your selection before confirming the upgrade."
              type="info"
              show-icon
              :closable="false">
            </el-alert>
          </div>
        </div>
      </div>

      <span slot="footer" class="dialog-footer">
        <el-button @click="closeUpgradeDialog" size="medium">
          {{ upgradeDialogState === 'empty' ? 'Close' : 'Cancel' }}
        </el-button>
        <el-button
          v-if="upgradeDialogState === 'ready'"
          type="primary"
          @click="confirmUpgrade"
          :disabled="!upgradeDialogData.selectedUpgrade"
          size="medium"
        >
          <i class="el-icon-check"></i>
          Confirm Upgrade
        </el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import _, { startCase } from 'lodash';
import moment from 'moment-timezone';
import { calculateMemberAge } from '@/utils/helpers';
import { mapGetters } from 'vuex';
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts.js';
import { clientNationalIds } from '@/utils/client-rules';
import { datadogRum } from '@datadog/browser-rum';
import { fileUploadAcceptString, resultTypes } from '../../../utils/constants';
import AdditionalQsDialog from './AdditionalQsDialog.vue';
import AdditionalQsDialogSaleableItem from './AdditionalQsDialogSaleableItem.vue';
import RenewPayment from './RenewPayment.vue';

export default {
  components: { AdditionalQsDialog, AdditionalQsDialogSaleableItem, RenewPayment },
  props: {
    value: Object,
  },
  data() {
    return {
      nationalId: +process.env.VUE_APP_NATIONAL_ID,
      key: 0,
      capitalize: _.capitalize,
      registrations: [],
      membershipToRegister: {},
      fileUploadAcceptString,
      get: _.get,
      showQsDialog: false,
      showRenewPayment: false,
      dialogValue: {
        filtersVisible: false,
        entityType: undefined,
        registration: undefined,
        regoDetails: undefined,
        member: undefined,
      },
      familyMSDialog: false,
      familyMSList: [],
      selectedFamilyMS: undefined,
      members: [],
      selectedRego: undefined,
      selectedFromRego: undefined,
      showQrCodeDialog: false,
      qrCode: '',
      showUpgradeDialog: false,
      upgradeDialogData: {
        registration: null,
        selectedUpgrade: null,
      },
      breakpoint: 'desktop',
      showQsDialogSI: false,
      dialogValueSI: {
        filtersVisible: false,
        entityType: undefined,
        registration: undefined,
        regoDetails: undefined,
        member: undefined,
      },
      mobileFilters: {
        entityType: '',
        expired: '',
      },
      entityTypes: ['membership', 'event'],
      searchQuery: '',
      filters: {
        entityType: '',
        approvalStatus: '',
        expired: '',
      },
      sortBy: 'reg-desc',
      seasonExpanded: {}, // Track which seasons are expanded
      viewMode: 'list', // Added viewMode property
    };
  },
  async mounted() {
    window.addEventListener('resize', this.handleResize);
    this.handleResize();
    const resMembers = await this.$http.get('/assemble/api/v1/account/portal/members-with-registrations');
    this.members = resMembers.data.data;
    const { regoId, regoProductId } = this.$route.params;
    if (this.value?._id) {
      this.$store.commit('loading', { loading: true, text: 'Loading...' });
      this.$http.get(`/assemble/api/v1/portal/registrations/members/${this.value._id}`)
        .then((res) => {
          this.registrations = _.map(res.data.data, (data) => ({
            ...data,
            results: _.get(data, 'results.0', {}),
            expanded: false // Initialize expanded state for show more/less
          }));
          this.registrations = this.registrations.map((rego) => _.assign(rego, { upgradingDropdown: false }));

          // Initialize all seasons as expanded
          const seasons = [...new Set(this.registrations.map((r) => r.season))];
          seasons.forEach((season) => {
            this.$set(this.seasonExpanded, season, true);
          });

          // eslint-disable-next-line no-restricted-syntax
          for (const membership of this.registrations) {
            if (_.get(membership, 'regoEntity.products') && membership.regoEntity.products.upgradeableMembershipList && membership.expired !== true) {
            // eslint-disable-next-line no-restricted-syntax
              for (const um of membership.regoEntity.products.upgradeableMembershipList) {
                if (um._id === regoId) {
                  const foundProduct = um.products.find((product) => product._id === regoProductId);
                  if (foundProduct) {
                    setTimeout(() => {
                      // eslint-disable-next-line prefer-destructuring
                      const position = (document.getElementsByClassName(`${membership._id}`))[0];
                      position.scrollIntoView({ behavior: 'smooth' });
                    }, 200);
                  }
                }
              }
            }
          }
          this.$store.commit('loading', false);
        })
        .catch((e) => {
          this.$message.error(e);
          this.$store.commit('loading', false);
        });
    }
  },
  methods: {
    getProductType(type) {
      // eslint-disable-next-line no-nested-ternary
      return type === 'indv' ? 'Individual' : type === 'team' ? 'Team' : type === 'family' ? 'Family' : '';
    },
    getRenewalDuration(rego) {
      const { durationYear, durationMonth, durationDay } = rego.regoEntity;
      if (durationYear > 0) return `${durationYear} year${durationYear > 1 ? 's' : ''}`;
      if (durationMonth > 0) return `${durationMonth} month${durationMonth > 1 ? 's' : ''}`;
      if (durationDay > 0) return `${durationDay} day${durationDay > 1 ? 's' : ''}`;
      return '';
    },
    isPaid(rego) {
      const {
        payments, pricing, charge, payAfter, offlinePayments, payAfterMeta, payLater
      } = rego;

      // Get the total required amount
      const total = _.get(pricing, `${rego.entityType}.total`, 0);

      // Calculate offline payments total
      const offlinePaymentTotal = offlinePayments ? _.sumBy(offlinePayments, 'amount') : 0;

      // Calculate total paid amount
      let totalPaid = 0;
      if (payAfter && payAfterMeta && payAfterMeta.amountPaid >= 0) {
        if (rego._id === 88047) {
          console.log('payAfterMeta', payAfterMeta);
        }
        totalPaid = payAfterMeta.amountPaid;
      } else if (payAfter && !payAfterMeta) {
        totalPaid = payments ? _.sumBy(payments, (p) => p.amount - (p.transactionFee || 0)) : 0;
      } else if (charge && payLater) {
        totalPaid = 0;
        if (charge && _.isArray(charge)) {
          charge.forEach((c) => {
            // Check if this charge has multiple registration IDs
            if (_.get(c, 'meta.registrationIds') && _.isArray(c.meta.registrationIds) && c.meta.registrationIds.length > 1) {
              // Multiple registrations in this charge - check transfers
              if (c.transfers && _.isArray(c.transfers)) {
                // Sum only transfers that match this registration ID
                const relevantTransfers = c.transfers.filter((transfer) => transfer.meta && transfer.meta.registrationId === rego._id);
                totalPaid += _.sumBy(relevantTransfers, (t) => t.amount - _.get(t, 'meta.transactionFee', 0));
              }
            } else {
              // Single registration or no registrationIds - use the full charge amount
              totalPaid += c.amount - _.get(c, 'meta.transactionFee', 0);
            }
          });
        }
      } else {
        return true;
      }

      // Add offline payments to total paid
      totalPaid += offlinePaymentTotal;
      if (rego._id === 88047) {
        console.log('totalPaid', totalPaid);
        console.log('total', total);
      }

      // Check if total paid is sufficient
      return totalPaid >= total;
    },
    getOutstanding(rego) {
      const {
        payments, pricing, charge, payAfter, offlinePayments, payAfterMeta, payLater
      } = rego;
      let totalPaid = 0;
      let total = 0;

      // Calculate total payment amount
      if (payAfter && payAfterMeta && payAfterMeta.amountPaid) {
        totalPaid = payAfterMeta.amountPaid;
      } else if (payAfter && !payAfterMeta) {
        totalPaid = payments ? _.sumBy(payments, (p) => p.amount - (p.transactionFee || 0)) : 0;
      } else if (charge && payLater) {
        totalPaid = 0;
        if (charge && _.isArray(charge)) {
          charge.forEach((c) => {
            // Check if this charge has multiple registration IDs
            if (_.get(c, 'meta.registrationIds') && _.isArray(c.meta.registrationIds) && c.meta.registrationIds.length > 1) {
              // Multiple registrations in this charge - check transfers
              if (c.transfers && _.isArray(c.transfers)) {
                // Sum only transfers that match this registration ID
                const relevantTransfers = c.transfers.filter((transfer) => transfer.meta && transfer.meta.registrationId === rego._id);
                totalPaid += _.sumBy(relevantTransfers, (t) => t.amount - _.get(t, 'meta.transactionFee', 0));
              }
            } else {
              // Single registration or no registrationIds - use the full charge amount
              totalPaid += c.amount - _.get(c, 'meta.transactionFee', 0);
            }
          });
        }
      }

      // Add offline payments to total paid
      const offlinePaymentTotal = offlinePayments && offlinePayments.length > 0
        ? _.sumBy(offlinePayments, 'amount') : 0;
      totalPaid += offlinePaymentTotal;

      total = _.get(pricing, `${rego.entityType}.total`, 0);
      return Math.max(0, total - totalPaid);
    },
    alreadyRenew(rego) {
      return rego.meta?.isRenewed;
    },
    getApprovalStatus(rego) {
      const { approvalStatus } = rego;
      if (approvalStatus === 'pending') {
        return 'Pending';
      }
      if (approvalStatus === 'approved') {
        return 'Approved';
      }
      if (approvalStatus === 'declined') {
        return 'Declined';
      }
      return this.getStatus(rego);
    },
    isExpired(rego) {
      const { expiry, expired } = rego;
      const current = moment().valueOf();
      if (expired) return true;
      if (current > expiry) return true;
      return false;
    },
    getAmountPaid(rego) {
      const { payAfterMeta, offlinePayments } = rego;
      const amountPaidOffline = offlinePayments ? _.sumBy(offlinePayments, 'amount') : 0;
      return amountPaidOffline + (payAfterMeta && payAfterMeta.amountPaid ? payAfterMeta.amountPaid : 0);
    },
    getAmountRemaining(rego) {
      const { payAfterMeta, offlinePayments } = rego;
      const amountPaidOffline = offlinePayments ? _.sumBy(offlinePayments, 'amount') : 0;
      return payAfterMeta && payAfterMeta.amountRemaining ? payAfterMeta.amountRemaining - amountPaidOffline : 0;
    },
    handleResize() {
      if (window.innerWidth < 992) {
        this.breakpoint = 'mobile';
      } else {
        this.breakpoint = 'desktop';
      }
    },
    handleStatusClicked(registration) {
      const isRenewable = _.get(registration, 'regoEntity.isRenewable');
      if (isRenewable) {
        this.makeRenewPayment(registration);
      }
    },
    getStatus(registration) {
      const { expiry, regoEntity } = registration;
      if (_.get(registration, 'deregistered')) return 'Deregistered';
      if (_.get(registration, 'deregistrationRequest[0].status') === 'pending') return 'Request Pending';
      if (expiry) {
        const current = moment().valueOf();
        if (current > expiry) return 'Expired';
      }
      if (_.get(regoEntity, 'isRenewable') && this.alreadyRenew(registration)) {
        return 'Renewed';
      }
      return 'Active';
    },
    getType(registration) {
      const { expiry, regoEntity } = registration;
      if (_.get(registration, 'deregistered')) return 'danger';
      if (_.get(registration, 'deregistrationRequest[0].status') === 'pending') return 'warning';
      if (_.get(regoEntity, 'isRenewable')) {
        return 'warning';
      }
      if (expiry) {
        const current = moment().valueOf();
        if (current > expiry) return 'danger';
      }
      return 'success';
    },
    beforeEnter(el) {
      // eslint-disable-next-line no-param-reassign
      el.style.height = '0';
    },
    enter(el) {
      // This ensures the element is displayed during the transition
      // eslint-disable-next-line no-param-reassign
      el.style.display = 'block';
      // eslint-disable-next-line no-param-reassign
      el.style.overflow = 'hidden';
      // eslint-disable-next-line no-param-reassign
      el.style.height = `${el.scrollHeight }px`;
    },
    beforeLeave(el) {
      // eslint-disable-next-line no-param-reassign
      el.style.height = `${el.scrollHeight }px`;
    },
    leave(el) {
      // Force a reflow, necessary to restart the animation
      // eslint-disable-next-line no-void
      void el.offsetHeight;
      // eslint-disable-next-line no-param-reassign
      el.style.height = '0';
    },
    toggleExpand(registration) {
      // Toggle the expanded state
      this.$set(registration, 'expanded', !registration.expanded);
    },
    productTypeFormatter(value) {
      // eslint-disable-next-line no-nested-ternary
      return value === 'indv' ? 'Individual'
        // eslint-disable-next-line no-nested-ternary
        : value === 'indv-to-team' ? 'Individual To Team'
          // eslint-disable-next-line no-nested-ternary
          : value === 'team' ? 'Team'
            : value === 'family' ? 'Family'
              : '';
    },
    getExpiry(row) {
      const { expiry, expired } = row;
      if (expired) return 'Yes';
      const expiryDate = moment(expiry).format('YYYY-MM-DD');
      const today = moment().format('YYYY-MM-DD');
      if (moment(expiryDate).isBefore(today)) return 'Yes';
      return 'No';
    },
    makeRenewPayment(rego) {
      this.$store.commit('loading', { loading: true, text: 'Validating renewal eligibility...' });

      // Set validation flag on the registration
      this.$set(rego, 'isValidatingRenewal', true);

      // Call validation endpoint first to check all requirements
      this.$http.post('/assemble/api/v1/portal/registrations/validate-renewal', {
        registrationId: rego._id,
        memberId: this.value._id
      })
        .then((response) => {
          const { data } = response.data;
          console.log('data', data);
          console.log('response.data', response.data);

          // If the response indicates missing prerequisite memberships
          if (data && !data.canRenew
              && data.details && data.details.membershipProductsRequired?.length) {
            console.log('missingRequiredMembership', data.details.missingRequiredMembership);
            // Get the list of required memberships
            const { membershipProductsRequired } = data.details;

            this.$store.commit('cart/backToRegoHistory', {
              memberId: this.value._id,
              backToRegoHistory: true,
            });

            this.$store.commit('loading', false);
            this.$message.info('You need to register for prerequisite memberships first.');

            // Log the redirection
            datadogRum.addAction('redirect_to_prerequisites', {
              memberId: this.value._id,
              regoId: rego._id,
              missingMemberships: membershipProductsRequired.length
            });

            // Set the active member before redirecting
            this.$store.commit('user/setActiveMember', this.value);

            // Redirect to NewFinder with the required memberships list
            const pathName = this.hasCustomEntity ? 'custom-finder' : 'finder';
            this.$router.push({
              name: pathName,
              params: {
                membershipRequiredProducts: membershipProductsRequired,
                memberId: this.value._id,
              }
            });
          } else {
            this.$store.commit('loading', false);
            // If validation passes, open the renewal dialog
            this.dialogValue = {
              filtersVisible: true,
              entityType: 'indv',
              registration: rego,
              regoDetails: this.registrations,
              member: this.value,
            };
            this.showRenewPayment = true;
          }
        })
        .catch((error) => {
          this.$store.commit('loading', false);
          // Display specific error message based on the validation failure reason
          const errorMessage = error.response?.data?.message || 'Unable to renew membership at this time.';
          this.$message.error(errorMessage);

          // Log the error for debugging
          datadogRum.addError(error, {
            message: 'Error validating membership renewal',
            tags: { feature: 'membership-renewal' }
          });
        })
        .finally(() => {
          // Clear validation flag regardless of success or failure
          this.$set(rego, 'isValidatingRenewal', false);
        });
    },
    async showQRCode(rego) {
      // show qr code using dialog
      if (!rego.files || rego.files.length === 0) {
        const findMsCard = await this.$http.get(`/assemble/api/v1/portal/registrations/${rego._id}/membership-card`);
        if (_.get(findMsCard, 'data.msCard.meta.qrCode')) {
          this.qrCode = findMsCard.data.msCard.meta.qrCode;
          this.showQrCodeDialog = true;
        } else {
          this.$message.error('No QR Code found');
        }
        return true;
      }
      const msCard = rego.files.find((file) => file.type === 'membership-card');
      if (!_.get(msCard, 'meta.qrCode')) return this.$message.error('No QR Code found');
      this.showQrCodeDialog = true;
      // eslint-disable-next-line no-return-assign
      this.qrCode = msCard ? msCard.meta.qrCode : '';
      return true;
    },
    showAQDialog(row) {
      this.dialogValue.filtersVisible = true;
      this.dialogValue.entityType = 'indv';
      this.dialogValue.registration = row;
      this.dialogValue.regoDetails = this.registrations;
      this.showQsDialog = true;
    },
    showAQDialogSI(row) {
      this.dialogValueSI.filtersVisible = true;
      this.dialogValueSI.entityType = 'indv';
      if (row.entityType === 'membership') {
        this.dialogValueSI.registration = _.get(row, 'pricing.membership.saleableItems[0]');
      } else {
        this.dialogValueSI.registration = _.get(row, 'pricing.event.saleableItems[0]');
      }
      this.dialogValueSI.regoDetails = this.registrations;
      this.dialogValueSI.row = row;
      this.showQsDialogSI = true;
    },
    closeDialog() {
      // Clear validation flag if it exists on the registration
      if (this.dialogValue && this.dialogValue.registration) {
        this.$set(this.dialogValue.registration, 'isValidatingRenewal', false);
      }

      this.dialogValue = {
        filtersVisible: false,
      };
      this.showQsDialog = false;
      this.showRenewPayment = false;
    },
    closeDialogSI() {
      this.dialogValueSI = {
        filtersVisible: false,
      };
      this.showQsDialogSI = false;
    },
    showUpgradeDialogHandler(registration) {
      this.upgradeDialogData.registration = registration;
      this.upgradeDialogData.selectedUpgrade = null;
      this.showUpgradeDialog = true;
    },
    closeUpgradeDialog() {
      this.upgradeDialogData = {
        registration: null,
        selectedUpgrade: null,
      };
      this.showUpgradeDialog = false;
    },
    confirmUpgrade() {
      if (this.upgradeDialogData.selectedUpgrade) {
        const { registration } = this.upgradeDialogData;
        registration.regoEntity.membershipToUpgrade = this.upgradeDialogData.selectedUpgrade;
        this.handleChangeMem(registration);
        this.closeUpgradeDialog();
      }
    },
    updateMeta({ meta, regoId }) {
      const foundIndex = this.registrations.findIndex((rego) => rego._id === regoId);
      if (foundIndex >= 0) this.registrations[foundIndex].meta = meta;
    },
    updateFiles({ files, regoId }) {
      const foundIndex = this.registrations.findIndex((rego) => rego._id === regoId);
      if (foundIndex >= 0) this.registrations[foundIndex].files = files;
    },
    makePayment(rego) {
      // push to route partial payment
      const pathName = this.hasCustomEntity ? 'custom-partial-payment' : 'partial-payment';
      this.$router.push({ name: pathName, params: { id: rego._id } });
    },
    priceFormatter(value) {
      return `$${value / 100}`;
    },
    async deregisterRequest(row) {
      const confirm = await this.$confirm('By clicking yes a request will be sent to the administrator to confirm the deregistration and issue a refund.', {
        confirmButtonText: 'Continue',
        cancelButtonText: 'Cancel',
      });

      if (confirm) {
        await this.$http.post('/assemble/api/v1/portal/deregistration-status/registration/request', {
          registrationId: row._id
        });
        // eslint-disable-next-line no-param-reassign
        const index = this.registrations.findIndex((rego) => rego._id === row._id);
        this.$set(this.registrations[index], 'deregistrationRequest', [{ status: 'pending' }]);

        this.$message.success('Request Sent!');

        this.$store.commit('loading', false);
      }
    },
    isShowableAnswer(name, questions) {
      const flattenQuestions = [];
      _.forEach((questions), (q) => {
        if (q.fieldName) {
          flattenQuestions.push({ type: q.type, question: q.question, fieldName: _.camelCase(q.fieldName) });
        }
        if (q.parentsDependentQsList && q.parentsDependentQsList.length) {
          _.forEach(q.parentsDependentQsList[0].children, (cq) => {
            flattenQuestions.push({ type: cq.type, question: cq.question, fieldName: _.camelCase(cq.fieldName) });
          });
        }
      });
      const question = _.find(flattenQuestions, (q) => (q.fieldName && q.fieldName === name));
      if (!question) return false;
      if (![1, 2].includes(question.type)) return false;
      return true;
    },
    isUpgradeEligible(rego) {
      if (rego.entityType === 'membership' && _.get(rego, 'regoEntity.products.isUpgradeable') && !rego.upgradingDropdown && !rego.expired) {
        const { dob, gender } = this.value.profile;
        const endOfYear = this.$store.getters['national/endOfYear'];
        const age = calculateMemberAge(dob, endOfYear);
        const membershipList = rego.regoEntity.products.upgradeableMembershipList;
        const checkEligible = membershipList.some((membership) => membership.products.some((product) => (age >= product.ageFrom && age <= product.ageTo && (product.gender === gender || product.gender === 'All')) || product.type === 'family' || (product.gender === 'Other' && (gender !== 'Male' || gender !== 'Female'))));
        return checkEligible;
      }
      return false;
    },
    getFilterdMemberships(upgradeableMembershipList) {
      if (!upgradeableMembershipList || !Array.isArray(upgradeableMembershipList)) {
        return [];
      }
      const { dob, gender } = this.value.profile;
      const endOfYear = this.$store.getters['national/endOfYear'];
      const age = calculateMemberAge(dob, endOfYear);
      const list = _.cloneDeep(upgradeableMembershipList);
      list.forEach((membership) => {
        if (membership.products && Array.isArray(membership.products)) {
          // eslint-disable-next-line no-param-reassign
          membership.products = membership.products.filter((product) => (age >= product.ageFrom && age <= product.ageTo && (product.gender === gender || product.gender === 'All')) || product.type === 'family' || (product.gender === 'Other' && (gender !== 'Male' || gender !== 'Female')));
        }
      });
      console.log('list', list);
      return list;
    },
    async downloadReceipt(registration) {
      let charges = [];
      try {
        const response = await this.$http.get(`/assemble/api/v1/portal/registrations/${registration._id}/receipt`);
        charges = response.data.data;
      } catch {
        this.$message.error('An error occured while downloading receipt');
      }

      if (pdfMake.vfs === undefined) {
        pdfMake.vfs = pdfFonts.pdfMake.vfs;
      }

      if (!charges.length) this.$message.success('This registration did not incur any cost thus no receipt was found');

      let logo = '';
      let isURL = false;
      if (process.env.VUE_APP_LOGO && process.env.VUE_APP_LOGO !== '') {
        logo = process.env.VUE_APP_LOGO;
        isURL = true;
      }

      const dd = await this.$http.post('/assemble/api/v1/portal/registrations/receipt-content', {
        member: this.value, charges, logo, isURL, type: 'member'
      });

      if (!isURL) {
        dd.data.data.content.unshift({ image: 'logo', width: [100], alignment: 'center' });
        dd.data.data.images = { logo: await this.getBase64Logo() };
      }

      pdfMake.createPdf(dd.data.data).download(`${registration._id}-${dd.data.chargeId}.pdf`);
    },
    splitTextIntoLines(ctx, text, maxWidth) {
      const words = text.split(' ');
      const lines = [];
      let currentLine = words[0];

      for (let i = 1; i < words.length; i++) {
        const word = words[i];
        const { width } = ctx.measureText(`${currentLine} ${word}`);

        if (width < maxWidth) {
          currentLine += ` ${word}`;
        } else {
          lines.push(currentLine);
          currentLine = word;
        }
      }

      lines.push(currentLine);
      return lines;
    },
    async generateMSCard(membershipDetails, memberProfilePics, entityPic) {
      const cardCanvas = document.createElement('canvas');
      cardCanvas.width = 500;
      cardCanvas.height = 300;
      const cardWidth = cardCanvas.width;
      const pageHeight = cardCanvas.height;
      const cardContext = cardCanvas.getContext('2d');
      const lineSpacing = 22;
      const columnSpacing = 260;
      const lineWidth = 2;
      let isLogoURL = false;
      let isProfileURL = false;
      let isEntityURL = false;

      let logoImage;
      let profileImage;
      let entityImage;
      let logoPromise; let profilePromise; let
        entityPromise;
      const promises = [];

      // Load Logo Image
      if (process.env.VUE_APP_LOGO && process.env.VUE_APP_LOGO !== '') {
        isLogoURL = true;
      }
      if (memberProfilePics && memberProfilePics !== '') {
        isProfileURL = true;
      }
      if (entityPic && entityPic !== '') {
        isEntityURL = true;
      }
      if (isLogoURL) {
        const logoContent = await this.$http.post('/assemble/api/v1/portal/registrations/image-content', { logo: process.env.VUE_APP_LOGO, isURL: true });
        logoImage = new Image();
        logoImage.src = logoContent.data.data.imageContent;
        // eslint-disable-next-line no-return-assign
        logoPromise = new Promise((resolve) => logoImage.onload = resolve);
        promises.push(logoPromise);
      }
      if (isProfileURL) {
        const profileContent = await this.$http.post('/assemble/api/v1/portal/registrations/image-content', { memberProfile: memberProfilePics, isURL: true });
        profileImage = new Image();
        profileImage.src = profileContent.data.data.memberImageContent;
        // eslint-disable-next-line no-return-assign
        profilePromise = new Promise((resolve) => profileImage.onload = resolve);
        promises.push(profilePromise);
      }
      if (isEntityURL) {
        const entityContent = await this.$http.post('/assemble/api/v1/portal/registrations/image-content', { entityLogo: entityPic, isURL: true });
        entityImage = new Image();
        entityImage.src = entityContent.data.data.entityImageContent;
        // eslint-disable-next-line no-return-assign
        entityPromise = new Promise((resolve) => entityImage.onload = resolve);
        promises.push(entityPromise);
      }

      await Promise.all(promises);

      // eslint-disable-next-line no-return-assign, no-param-reassign
      // await Promise.all([logoImage, memberProfilePics, entityPic].map((image) => new Promise((resolve) => image.onload = resolve)));

      // Draw the card background with border radius on the canvas
      cardContext.fillStyle = '#fff';
      cardContext.beginPath();
      cardContext.moveTo(20, 0);
      cardContext.lineTo(cardWidth - 20, 0);
      cardContext.quadraticCurveTo(cardWidth, 0, cardWidth, 20);
      cardContext.lineTo(cardWidth, pageHeight - 20);
      cardContext.quadraticCurveTo(cardWidth, pageHeight, cardWidth - 20, pageHeight);
      cardContext.lineTo(20, pageHeight);
      cardContext.quadraticCurveTo(0, pageHeight, 0, pageHeight - 20);
      cardContext.lineTo(0, 20);
      cardContext.quadraticCurveTo(0, 0, 20, 0);
      cardContext.closePath();
      cardContext.fill();

      // Draw the card border
      // Add border to the card
      cardContext.lineWidth = 2; // Set the border thickness
      cardContext.strokeStyle = '#000'; // Set the border color
      cardContext.stroke(); // Draw the border

      // Draw member profile logo on the top left
      const profileLogoWidth = 100;
      const profileLogoHeight = 100;
      const profileLogoX = 10;
      const profileLogoY = 10;
      cardContext.globalAlpha = 1;
      if (profileImage) {
        cardContext.drawImage(profileImage, profileLogoX, profileLogoY, profileLogoWidth, profileLogoHeight);
      } else {
        cardContext.fillStyle = '#888';
        cardContext.fillRect(profileLogoX, profileLogoY, profileLogoWidth, profileLogoHeight);
      }

      // Draw entity logo on the top right
      const entityLogoWidth = 100;
      const entityLogoHeight = 100;
      const entityLogoX = cardWidth - entityLogoWidth - 10;
      const entityLogoY = 10;

      if (entityImage) {
        cardContext.drawImage(entityImage, entityLogoX, entityLogoY, entityLogoWidth, entityLogoHeight);
      } else {
        cardContext.drawImage(logoImage, entityLogoX, entityLogoY, entityLogoWidth, entityLogoHeight);
      }

      const maxLineWidth = 260; // Maximum line width in pixels for the first level text

      // Draw entity created first level limited and second level limited in the top middle
      // First level with h2-like font size and black color
      cardContext.font = 'bold 22px BrownStd-Regular';
      cardContext.fillStyle = '#000'; // Black color
      // Adjust the starting Y position for the text to be closer to the top, with a small margin
      const textStartX = profileLogoX + profileLogoWidth + 10; // Start text from the right edge of the profile photo with a 10px margin
      const entityTextY = profileLogoY + 20; // Start text 10px below the top edge of the profile logo

      // Use the splitTextIntoLines function to handle potentially long text for the first level
      const firstLevelLines = this.splitTextIntoLines(cardContext, membershipDetails.entityCreatedFirstLevelLimited, maxLineWidth);
      firstLevelLines.slice(0, 2).forEach((line, index) => { // Ensure only up to 2 lines are drawn for the first level
        cardContext.fillText(line, textStartX, entityTextY + (index * 25));
      });

      // Calculate Y position for second level based on how many lines were used for first level
      const secondLevelStartY = entityTextY + (firstLevelLines.length * 25) + 5; // Adjust Y position as needed for second line, with some spacing

      // Draw entity created second level limited in gray color and normal font size, potentially split into 2 lines
      cardContext.font = '16px BrownStd-Regular'; // Normal size for second level
      cardContext.fillStyle = '#888'; // Gray color for second level

      // Use the splitTextIntoLines function for the second level as well
      const secondLevelLines = this.splitTextIntoLines(cardContext, membershipDetails.entityCreatedSecondLevelLimited, maxLineWidth);
      secondLevelLines.slice(0, 2).forEach((line, index) => { // Ensure only up to 2 lines are drawn for the second level
        cardContext.fillText(line, textStartX, secondLevelStartY + (index * 20));
      });

      // Draw membership details at the bottom
      cardContext.fillStyle = '#000';
      cardContext.font = 'bold 22px BrownStd-Regular';
      const membershipNameLines = this.splitTextIntoLines(cardContext, `${membershipDetails.membershipName}`, 450);
      membershipNameLines.forEach((line, index) => {
        cardContext.fillText(line, 10, pageHeight - 150 + index * lineSpacing);
      });

      // // a line to display entity name
      // cardContext.font = '16px BrownStd-Regular';
      // cardContext.fillStyle = '#888';
      // cardContext.fillText(`${membershipDetails.entity}`, 10, pageHeight - 150 + 1 * lineSpacing); // Adjust the vertical position as needed

      // first column
      cardContext.font = '18px BrownStd-Regular';
      // membership details label, gray text color
      cardContext.fillStyle = '#aaa';
      cardContext.fillText('Membership Details', 10, pageHeight - 150 + 2 * lineSpacing); // Adjust the vertical position as needed
      cardContext.fillStyle = '#000';
      cardContext.font = '16px BrownStd-Regular';
      // cardContext.fillText(`Entity: ${membershipDetails.entity}`, 10, pageHeight - 150 + 3 * lineSpacing); // Adjust the vertical position as needed
      cardContext.fillText(`Member Type: ${membershipDetails.memberType}`, 10, pageHeight - 150 + 3 * lineSpacing); // Adjust the vertical position as needed
      cardContext.fillText(`Season: ${membershipDetails.season}`, 10, pageHeight - 150 + 4 * lineSpacing); // Adjust the vertical position as needed
      // eslint-disable-next-line no-nested-ternary
      const expiryText = membershipDetails.deregistered || membershipDetails.expired
        ? 'Expired'
        : membershipDetails.expiry
          ? `Exp: ${this.msDateFormatter(membershipDetails.expiry)}`
          : '';
      cardContext.fillText(`${expiryText}`, 10, pageHeight - 150 + 5 * lineSpacing); // Adjust the vertical position as needed

      // Draw vertical line between columns
      cardContext.beginPath();
      cardContext.moveTo(columnSpacing - 20, pageHeight - 150 + 2 * lineSpacing);
      cardContext.lineTo(columnSpacing - 20, pageHeight - 150 + 6 * lineSpacing);
      cardContext.lineWidth = lineWidth;
      cardContext.strokeStyle = '#888';
      cardContext.stroke();

      // second column
      cardContext.font = '18px BrownStd-Regular';
      // membership details label, gray text color
      cardContext.fillStyle = '#aaa';
      cardContext.fillText('Member Details ', columnSpacing, pageHeight - 150 + 2 * lineSpacing); // Adjust the vertical position as needed
      cardContext.fillStyle = '#000';
      cardContext.font = '16px BrownStd-Regular';
      cardContext.fillText(`Name: ${membershipDetails.memberName}`, columnSpacing, pageHeight - 150 + 3 * lineSpacing); // Adjust the vertical position as needed
      cardContext.fillText(`Member ID: ${membershipDetails.memberId}`, columnSpacing, pageHeight - 150 + 4 * lineSpacing); // Adjust the vertical position as needed
      cardContext.fillText(`Member Number: ${membershipDetails.memberNumber}`, columnSpacing, pageHeight - 150 + 5 * lineSpacing); // Adjust the vertical position as needed
      const dobFormat = +process.env.VUE_APP_NATIONAL_ID === 15 ? 'YYYY' : 'DD/MM/YYYY';
      cardContext.fillText(`DOB: ${moment(membershipDetails.memberDob).format(dobFormat)}`, columnSpacing, pageHeight - 150 + 6 * lineSpacing); // Adjust the vertical position as needed

      // Reset alpha for the entity logo
      // Draw card watermark (background image)
      cardContext.globalAlpha = 0.1;
      cardContext.drawImage(logoImage, 0, 0, cardWidth, pageHeight);
      cardContext.globalAlpha = 1;

      // create a new image object
      return new Promise((resolve) => {
        cardCanvas.toBlob((blob) => {
          resolve(blob);
        }, 'image/png');
      });
    },
    async downloadMSCard(val) {
      const { type, value: registration } = val;
      let confirmText = 'Membership card is not available yet. Do you want to generate it now?';
      if (type === 'regenerate') {
        confirmText = 'Do you want to regenerate the Membership Card?';
      }

      const msCardRes = await this.$http.get(`/assemble/api/v1/portal/registrations/${registration._id}/membership-card`);
      let msCard = msCardRes.data.data;
      const hasMsCard = msCard && msCard !== '';
      const msCardDetails = msCardRes.data.msCard;
      if (msCardDetails) {
        // check meta field newChange
        if (msCardDetails.meta.newChange) {
          confirmText = 'Membership details have been updated. Select yes to generate a new Membership Card.';
        }
      }
      if (!hasMsCard || msCardDetails.meta.newChange || type === 'regenerate') {
        this.$confirm(confirmText, 'Confirm', {
          distinguishCancelAndClose: true,

          confirmButtonText: 'Yes',

          cancelButtonText: 'No',
        })
          .then(async () => {
            this.$store.commit('loading', true);
            const currMember = this.members.find((member) => member._id === registration.memberId);

            const currentSeason = this.$store.getters['national/currentSeason'];
            const currMemberProfilePic = _.get(currMember, 'meta.imgUrl');
            const entityPic = _.get(registration, 'regoEntity.meta.imgUrl');
            const memberMembership = this.$store.getters['user/getActiveMemberRegos'];
            const { orgtree } = registration;
            let expectedExpiryDate;
            if (registration.renewFrom) {
              const renewalRego = _.find(memberMembership, (r) => r._id === registration.renewFrom);
              if (renewalRego) {
                expectedExpiryDate = renewalRego.expiry;
              }
            }
            // Manually create an array in the desired order from lowest to highest
            const orderedOrgtree = [
              orgtree.club, // Start with the lowest level
              orgtree.association,
              orgtree.state,
              orgtree.national
            ].filter((org) => org !== undefined); // This will remove any undefined levels, in case some levels do not exist

            const entityCreatedFirstLevel = orderedOrgtree[0] ? orderedOrgtree[0].name : '';
            const entityCreatedSecondLevel = orderedOrgtree[1] ? orderedOrgtree[1].name : '';
            const entityCreatedFirstLevelLimited = entityCreatedFirstLevel;
            const entityCreatedSecondLevelLimited = entityCreatedSecondLevel;

            // Reverse the array to start from club and then move up, and take the first two entities
            // const entityCreated = orderedOrgtree.slice(0, 2).map((org) => org.name).join(', ');
            // const entityCreatedLimited = entityCreated.length > 45 ? `${entityCreated.substring(0, 45)}...` : entityCreated;
            const getExpiryDate = () => {
              if (registration.useDuration) {
                if (expectedExpiryDate) {
                  return moment(expectedExpiryDate)
                    .add(registration.durationYear, 'year')
                    .add(registration.durationMonth, 'month')
                    .add(registration.durationDay, 'day')
                    .valueOf();
                }

                return moment()
                  .add(registration.durationYear, 'year')
                  .add(registration.durationMonth, 'month')
                  .add(registration.durationDay, 'day')
                  .valueOf();
              }
              return _.get(registration, 'endDate') || _.get(registration, 'expiry');
            };
            const membershipDetails = {
              membershipName: registration.fromImport ? 'Legacy Membership' : registration.regoEntity.name,
              membershipId: registration.fromImport ? '0' : registration.regoEntity._id,
              expiry: getExpiryDate(),
              memberType: registration.memberType,
              season: currentSeason,
              memberId: registration.memberId,
              memberName: `${currMember.profile.firstName} ${currMember.profile.lastName}`,
              memberDob: currMember.profile.dob,
              memberNumber: _.get(currMember, 'meta.memberNumber', ''),
              entityCreatedFirstLevelLimited,
              entityCreatedSecondLevelLimited,
              deregistered: registration.deregistered,
              expired: registration.expired,
            };
            msCard = await this.generateMSCard(membershipDetails, currMemberProfilePic, entityPic);
            // get presigned url
            const presignedUrl = await this.$http.post('/assemble/api/v1/portal/membership-card/presigned-url',
              {
                fileName: `membership-card-${registration.memberId}-${registration.entityId}-${_.get(registration, 'regoEntity.products._id', '')}.png`,
                folder: 'membership-cards',
                membershipDetails,
                registrationId: registration._id,
                notFromRego: true,
                regenerating: !!msCardDetails?.meta?.newChange,
              });

            const { url, s3Path } = presignedUrl.data.data;

            if (s3Path) {
              _.assign(registration, { membershipCard: s3Path });
            }

            await fetch(url, {
              method: 'PUT',
              body: msCard,
              headers: { 'Content-Type': '*/*' },
            });
            this.$store.commit('loading', false);
            this.$message.success('Membership card has been generated successfully');
          })
          .catch((e) => {
            datadogRum.addError(e, {
              message: 'Error generating membership card',
              tags: { feature: 'membership-card' }
            });
          });
        return;
      }
      // msCard is in base64 format already
      if (type === 'png') {
        const link = document.createElement('a');
        link.download = `${registration._id}.png`;

        // Convert to blob with base64
        const blob = this.dataURItoBlob(msCard);
        const blobUrl = URL.createObjectURL(blob);
        // Set the link href to the blob url
        link.href = blobUrl;
        // Append the link to the body
        document.body.appendChild(link);
        // Trigger the click event on the link
        link.click();
      } else if (type === 'pdf') {
        pdfMake.vfs = pdfFonts.pdfMake.vfs;
        const dd = {
          content: [
            { image: msCard, width: [300], alignment: 'center' },
          ],
        };
        pdfMake.createPdf(dd).download(`${registration._id}.pdf`);
      }
    },

    dataURItoBlob(dataURI) {
      const byteString = atob(dataURI.split(',')[1]);
      const mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0];
      const ab = new ArrayBuffer(byteString.length);
      const ia = new Uint8Array(ab);

      for (let i = 0; i < byteString.length; i += 1) {
        ia[i] = byteString.charCodeAt(i);
      }

      return new Blob([ab], { type: mimeString });
    },
    async getImageBase64(url) {
      return new Promise((resolve) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
          const canvas = document.createElement('canvas');
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0);
          resolve(canvas.toDataURL('image/png'));
        };
        img.src = url;
      });
    },
    getResultLabel: (type) => _.find(resultTypes, { type }).label,
    beforeUpload(file) {
      const size = file.size / 1024 / 1024;
      if (size > 10) {
        this.$message.error('File size is more than 10mb');
        return false;
      }
      return true;
    },
    onError(error) {
      if (error.message) {
        const json = JSON.parse(error.message);
        this.$message.error(json.message || 'There has been an error');
      } else {
        this.$message.error('There has been an error');
      }
    },
    entityFormatter(row) {
      if (!row || !row.regoEntity) return '';

      if (!row.fromImport) {
        if (row.team && row.team.name) return `${row.team.name} - ${_.get(row, 'regoEntity.name')}`;
        return _.get(row, 'regoEntity.name', '');
      }
      return row.meta && row.meta.productName ? row.meta.productName : '';
    },
    dateFormatter(timestamp) {
      return moment(timestamp).tz(this.timezone).format('DD MMM YYYY, HH:mm');
    },
    msDateFormatter(timestamp) {
      // return in this type mm/yyyy
      return moment(timestamp).tz(this.timezone).format('DD/MM/YYYY');
    },
    handleChangeMem(rego) {
      const registrations = this.$store.getters['cart/registrations'];
      const [id, productId] = _.get(rego, 'regoEntity.membershipToUpgrade');
      const memberId = this.value._id;
      this.$confirm('Are you sure you want to upgrade this membership?', 'Confirm', {
        distinguishCancelAndClose: true,
        confirmButtonText: 'Yes',
        cancelButtonText: 'No',
      })
        .then(() => {
          this.$store.commit('loading', { loading: true, text: 'Loading...' });
          this.$http.post(`/assemble/api/v1/portal/registrations/search/entities/${id}/products/${productId}`)
            .then((res) => {
              const { result, transactionFee } = res.data.data;
              const [resutObject] = result;
              // eslint-disable-next-line no-param-reassign
              this.membershipToRegister = resutObject;
              const {
                _id: entityId, productType, memberType, ageFrom, ageTo
              } = this.membershipToRegister;

              if (resutObject.productType === 'family') {
                this.checkFamily(resutObject, rego);
              } else {
                this.membershipToRegister = resutObject;

                const foundRego = _.find(registrations, (r) => r.entityId === entityId
                                && r.memberId === memberId
                                && r.productType === productType
                                && r.ageFrom === ageFrom
                                && r.ageTo === ageTo
                                && r.memberType === memberType);
                if (foundRego) this.$message.error('Registration for this member has already been added to cart.');
                else {
                  this.$store.commit('cart/setTransactionFee', transactionFee);
                  // eslint-disable-next-line no-param-reassign
                  delete rego.upgradingDropdown;
                  const pathName = this.hasCustomEntity ? 'custom-membership-register-individual' : 'membership-register-individual';
                  this.$router.push({
                    name: pathName,
                    params: { memberId, selectedMembership: this.membershipToRegister, upgradeFromMembership: rego },
                  });
                }
                this.$store.commit('loading', false);
              }
            }).catch((e) => {
              datadogRum.addError(e, {
                message: 'Error upgrading membership',
                tags: { feature: 'membership-upgrade' }
              });
              this.$message.error(e.response.data.message);
              this.$store.commit('loading', false);
            });
        })
        .catch(() => {});
    },
    async handleChange(file, fileList, config) {
      const fileIndex = fileList.findIndex((fl) => fl.name === file.name);
      try {
        this.$store.commit('loading', { loading: true, text: 'Loading...' });
        const res = await this.$http.post('/assemble/api/v1/portal/files/presigned-url', { fileName: file.name });
        const { url, name } = res.data.data;
        await fetch(url, {
          method: 'PUT',
          body: file.raw,
          headers: { 'Content-Type': '*/*' },
        });
        const nameOnly = name.split('.')[0];
        const fileTypeOnly = name.split('.')[name.split('.').length - 1];
        // eslint-disable-next-line no-param-reassign
        fileList[fileIndex].name = nameOnly;
        // eslint-disable-next-line no-param-reassign
        fileList[fileIndex].type = fileTypeOnly;
        this.$store.commit('loading', false);
      } catch (e) {
        this.$message.error('There has been an error');
      }
      this.$store.commit('loading', { loading: true, text: 'Loading...' });

      // After uploading via s3 presigned url, update file-uploads collection
      this.$http.post(this.fileUpdateUrl, {
        originalName: file.raw.name.split('.')[0],
        fileName: file.name,
        fileType: file.type,
        ...config
      }).then((res) => {
        const { fileId, fileName } = res.data.data;
        this.$message.success('File uploaded successfully');
        // eslint-disable-next-line no-param-reassign
        fileList[fileIndex]._id = fileId;
        // eslint-disable-next-line no-param-reassign
        fileList[fileIndex].name = `${fileName} Uploaded At: ${this.dateFormatter(this.moment())}`;
      }).catch((e) => {
        datadogRum.addError(e, {
          message: 'Error uploading file',
          tags: { feature: 'file-upload' }
        });
        this.$message.error('There has been an error uploading the file');
      }).finally(() => {
        this.$store.commit('loading', false);
      });
    },
    removeFile(file) {
      const fileId = file._id;
      if (fileId) {
        return this.$confirm('You are about to permanently delete this file. Are you sure you want to continue?', 'Warning', { type: 'warning' })
          .then(() => {
            this.$store.commit('loading', { loading: true, text: 'Loading...' });
            this.$http.delete(`/assemble/api/v1/portal/files/${fileId}`)
              .then(() => {
                this.$message.success('Success!');
                this.$store.commit('loading', false);
              })
              .catch(() => {
                this.$message.error('Error deleting file');
                this.$store.commit('loading', false);
              });
          })
          .catch();
      } if (file) {
        // Catch for when the file hasn't been uploaded yet
        return true;
      }
      return false;
    },
    getUploadExpiry(rego) {
      const { entityType } = rego;
      if (entityType === 'event') {
        const [event] = rego.event;
        const { regoEndDate, isActive } = event;
        if (moment().valueOf() <= regoEndDate && isActive) return false;
        return true;
      }
      return false;
    },
    getUploadTip(rego, field) {
      const question = rego.additionalQs.find((q) => q.fieldName === field);
      return question
        ? question.question
        : 'Uploaded files must be 10mb or less';
    },
    submitUpload(ref) {
      this.$store.commit('loading', { loading: true, text: 'Loading...' });
      const uploadComponent = this.$refs[ref][0];
      uploadComponent.submit();
    },
    previewUpload(file) {
      const { _id } = file;
      this.$http.get(`/assemble/api/v1/portal/files/${_id}/signed`)
        .then((res) => {
        // Ask the user if they want to open the file
          this.$confirm('Do you want to view the file?', 'Confirm', {
            confirmButtonText: 'Yes',
            cancelButtonText: 'No',
            type: 'warning'
          }).then(() => {
          // User confirmed, open the link in a new tab
            window.open(res.data.url, '_blank');
          }).catch(() => {
          // User cancelled
            this.$message.info('File preview cancelled.');
          });
        })
        .catch((err) => {
          this.$message.error(err);
        });
    },
    groupedUploads(data) {
      const grouped = _.groupBy(data, (file) => file.meta.additionalQuestion);
      const out = Object.keys(grouped).map((group) => ([group, grouped[group]]));
      return out;
    },
    isNotExpired(rego) {
      if (+this.moment() <= _.get(rego, 'event[0].regoEndDate') && _.get(rego, 'event[0].isActive')) {
        return true;
      }
      return false;
    },
    handleDropdown(rego, val) {
      if (val === 'open') {
        this.$set(rego, 'upgradingDropdown', true);
        this.$set(rego, 'upgradingTimestamp', Date.now());
      } else {
        this.$set(rego, 'upgradingDropdown', false);
        this.$set(rego, 'upgradingTimestamp', Date.now());
      }
      this.key++;
      this.$nextTick(() => {
        this.$forceUpdate();
      });
    },
    getClassnameById({ row }) {
      return `${row._id}`;
    },
    joinTag(vals) {
      return vals ? _.join(_.map(vals, (v) => v.name), ', ') : '';
    },
    getQuestion(metaName, additionalQs) {
      const flattenQuestions = [];
      _.forEach((additionalQs), (q) => {
        if (q.fieldName) {
          flattenQuestions.push({ question: q.question, fieldName: _.camelCase(q.fieldName) });
        }
        if (q.parentsDependentQsList && q.parentsDependentQsList.length) {
          _.forEach(q.parentsDependentQsList[0].children, (cq) => {
            flattenQuestions.push({ question: cq.question, fieldName: _.camelCase(cq.fieldName) });
          });
        }
      });
      const aQ = flattenQuestions.find((data) => data.fieldName === metaName);
      return _.get(aQ, 'question');
    },
    metaFormat(value) {
      if (value && _.isArray(value)) return _.join(value, ', ');
      return value;
    },
    getBase64Logo() {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.setAttribute('crossOrigin', 'anonymous');
        img.onload = () => {
          const canvas = document.createElement('canvas');
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0);
          const dataURL = canvas.toDataURL('image/png');
          resolve(dataURL);
        };
        img.onerror = (error) => {
          reject(error);
        };
        img.src = `${ this.logo }?r=${ Math.floor(Math.random() * 100000)}`;
      });
    },
    checkFamily(rego, fromRego) {
      this.selectedRego = rego;
      this.selectedFromRego = fromRego;
      this.familyMSList = _.get(rego, 'products[0].familyMS');
      _.forEach(this.familyMSList, (fms) => {
        // eslint-disable-next-line no-param-reassign
        fms.availableMembers = this.getAvailableMembers(fms);
      });
      this.familyMSDialog = true;
    },
    initRegoFamily(familyProduct) {
      const mappedFamilyRego = _.map(familyProduct, (fp) => ({
        ...this.selectedRego,
        ...fp,
        _id: this.selectedRego._id,
        gender: fp.gender,
        ageFrom: fp.ageFrom,
        ageTo: fp.ageTo,
        memberType: fp.memberType,
        productId: _.get(this, 'selectedRego.products[0]._id'),
        products: [
          {
            ...this.selectedRego.products[0],
            gender: fp.gender,
            ageFrom: fp.ageFrom,
            ageTo: fp.ageTo,
            memberType: fp.memberType,
            familyProductId: fp._id,
          }
        ]
      }));

      this.selectedRego.familyRego = mappedFamilyRego;

      const pathName = this.hasCustomEntity ? 'custom-membership-register-individual' : 'membership-register-individual';
      this.$router.push({
        name: pathName,
        params: { selectedMembership: this.selectedRego, upgradeFromMembership: this.selectedFromRego },
      });
    },
    cancelFamilyRego() {
      _.each(this.familyMSList, (fam) => {
        // eslint-disable-next-line no-param-reassign
        fam.memberId = undefined;
      });
      this.familyMSDialog = false;
      this.selectedFamilyMember = false;
    },
    getAvailableMembers(product) {
      const members = _.filter(this.members, (member) => {
        const endOfYear = this.$store.getters['national/endOfYear'];
        const memberAge = calculateMemberAge(member.profile.dob, endOfYear);
        return memberAge >= product.ageFrom || memberAge <= product.ageTo || (member.profile.gender === product.gender);
      });

      // required memberships
      const requiredMSProducts = _.map(this.selectedRego.products[0].membershipProductsRequired, (ms) => ms.products._id);

      const mappedMembers = [];

      if (requiredMSProducts && requiredMSProducts.length) {
        _.forEach(members, (member) => {
          const mappedProductId = _.map(member.registration, (rego) => rego.productId);
          const intersection = _.intersection(requiredMSProducts, mappedProductId);
          if (intersection.length === requiredMSProducts.length) mappedMembers.push(member);
        });
      }
      return _.get(this, 'selectedRego.products[0].isMembershipRequired') ? mappedMembers : members;
    },
    filterMobileRegistrations() {
      return this.registrations.filter((registration) => {
        // Check entity type filter
        const entityTypeMatch = this.mobileFilters.entityType === '' || registration.entityType === this.mobileFilters.entityType;

        // Check expiry filter
        let expiryMatch = true;
        if (this.mobileFilters.expired !== '') {
          const current = moment().valueOf();
          const isActuallyExpired = registration.expired || (registration.expiry && current > registration.expiry);
          expiryMatch = isActuallyExpired === this.mobileFilters.expired;
        }

        return entityTypeMatch && expiryMatch;
      });
    },
    getApprovalTagType(registration) {
      const { approvalStatus } = registration;

      if (approvalStatus === 'pending') {
        return 'warning';
      } if (approvalStatus === 'approved') {
        // For approved registrations, use the regular expiry/activity status color
        return this.getType(registration);
      } if (approvalStatus === 'declined') {
        return 'danger';
      }

      // Default to showing the regular status color
      return this.getType(registration);
    },
    calculatePaymentPercentage(rego) {
      const {
        payments, pricing, charge, payAfter, offlinePayments
      } = rego;

      // Get the total required amount
      const total = _.get(pricing, `${rego.entityType}.total`, 0);
      if (total === 0) return 100; // If total is 0, it's fully paid

      // Calculate total paid amount
      let totalPaid = 0;
      if (payAfter) {
        totalPaid = payments ? _.sumBy(payments, (p) => p.amount - (p.transactionFee || 0)) : 0;
      } else {
        totalPaid = charge ? _.sumBy(charge, (c) => c.amount - _.get(c, 'meta.transactionFee', 0)) : 0;
      }

      // Add offline payments
      const offlinePaymentTotal = offlinePayments ? _.sumBy(offlinePayments, 'amount') : 0;
      totalPaid += offlinePaymentTotal;

      // Calculate percentage paid (limit to 100%)
      return Math.min(100, Math.round((totalPaid / total) * 100));
    },
    getEntityTagType(entityType) {
      if (entityType === 'membership') return 'primary';
      if (entityType === 'event') return 'success';
      return 'info';
    },
    // Add method to toggle season expansion
    toggleSeason(season) {
      this.$set(this.seasonExpanded, season, !this.seasonExpanded[season]);
    },
    getRowClass({ row }) {
      if (row.approvalStatus === 'pending') {
        return 'pending-row';
      } if (row.approvalStatus === 'approved') {
        return 'approved-row';
      } if (row.approvalStatus === 'declined') {
        return 'declined-row';
      }
      return '';
    },
  },
  computed: {
    deregistrationDisabled() {
      const misc = this.$store.getters['national/getMisc'] || {};
      const enabled = misc?.deregistrationEnabled ?? true;
      return !enabled;
    },
    hasCustomEntity() {
      const customEntity = this.$store.getters['national/getEntity'];
      if (customEntity && customEntity.level && customEntity.name) {
        return true;
      }
      return false;
    },
    latestDialogValue() {
      return this.dialogValue;
    },
    latestDialogValueSI() {
      return this.dialogValueSI;
    },
    logo() {
      // eslint-disable-next-line global-require
      const logo = process.env.VUE_APP_LOGO && process.env.VUE_APP_LOGO !== '' ? process.env.VUE_APP_LOGO : require('@/assets/logos/Assemble_Logo_RGB_Positive.png');
      // eslint-disable-next-line global-require
      const logoMark = process.env.VUE_APP_LOGO_MARK && process.env.VUE_APP_LOGO_MARK !== '' ? process.env.VUE_APP_LOGO_MARK : require('@/assets/logos/mark/Assemble_Logo_Mark_RGB_Positive.png');
      return logo || logoMark;
    },
    nationalLogoMark() {
      // eslint-disable-next-line global-require
      return process.env.VUE_APP_LOGO_MARK && process.env.VUE_APP_LOGO_MARK !== '' ? process.env.VUE_APP_LOGO_MARK : require('@/assets/logos/mark/Assemble_Logo_Mark_RGB_Mono_Positive.png');
    },
    nationalLogoOnly() {
      // eslint-disable-next-line global-require
      return process.env.VUE_APP_LOGO_ONLY && process.env.VUE_APP_LOGO_ONLY !== '' ? process.env.VUE_APP_LOGO_ONLY : require('@/assets/logos/mark/Assemble_Logo_Mark_RGB_Mono_Positive.png');
    },
    customMemberDetailForm() {
      const nationalId = _.includes(clientNationalIds, +process.env.VUE_APP_NATIONAL_ID) ? process.env.VUE_APP_NATIONAL_ID : 0;
      // eslint-disable-next-line
      return () => import('@/views/app/clients/' + nationalId + '-member-profile-extras.vue');
    },
    accept() {
      return fileUploadAcceptString;
    },

    isNSWISA() {
      return +process.env.VUE_APP_NATIONAL_ID === 1;
    },
    memberIdLabel() {
      return +process.env.VUE_APP_NATIONAL_ID === 1 ? 'NSWISA Member ID' : 'Member ID';
    },
    fileUpdateUrl() {
      return `${process.env.VUE_APP_API_URL}/assemble/api/v1/portal/files/registration`;
    },
    timezone() {
      return this.$store.getters['national/timezone'];
    },
    ...mapGetters('user', [
      'uploadHeaders',
    ]),
    selectedMember() {
      const hasSelected = _.every(this.familyMSList, (fm) => fm.memberId !== undefined);

      return hasSelected;
    },
    filteredMobileRegistrations() {
      return this.filterMobileRegistrations();
    },
    // New computed property for filtered registrations in the redesigned desktop view
    filteredRegistrations() {
      if (!this.registrations || !Array.isArray(this.registrations)) return [];

      // First filter the registrations
      const filtered = this.registrations.filter((registration) => {
        if (!registration) return false;

        // Check search query
        const entityName = this.entityFormatter(registration) || '';
        const nameMatch = this.searchQuery === ''
          || entityName.toLowerCase().includes(this.searchQuery.toLowerCase());

        // Check entity type filter
        const entityTypeMatch = this.filters.entityType === ''
          || registration.entityType === this.filters.entityType;

        // Check approval status filter
        const approvalMatch = this.filters.approvalStatus === ''
          || registration.approvalStatus === this.filters.approvalStatus;

        // Check expiry filter
        let expiryMatch = true;
        if (this.filters.expired !== '') {
          const current = moment().valueOf();
          const isActuallyExpired = registration.expired || (registration.expiry && current > registration.expiry);
          expiryMatch = isActuallyExpired === this.filters.expired;
        }

        return nameMatch && entityTypeMatch && approvalMatch && expiryMatch;
      });

      // Then sort the filtered results
      if (!this.sortBy) return filtered;

      return [...filtered].sort((a, b) => {
        switch (this.sortBy) {
          case 'entity-asc':
            return (this.entityFormatter(a) || '').localeCompare(this.entityFormatter(b) || '');
          case 'entity-desc':
            return (this.entityFormatter(b) || '').localeCompare(this.entityFormatter(a) || '');
          case 'reg-desc':
            return (b.createdAt || 0) - (a.createdAt || 0);
          case 'reg-asc':
            return (a.createdAt || 0) - (b.createdAt || 0);
          case 'exp-desc':
            return (b.expiry || 0) - (a.expiry || 0);
          case 'exp-asc':
            return (a.expiry || 0) - (b.expiry || 0);
          default:
            return 0;
        }
      });
    },
    // Group registrations by season
    registrationsBySeasonDesktop() {
      if (!this.filteredRegistrations || !Array.isArray(this.filteredRegistrations)) return [];

      // Group by season
      const grouped = _.groupBy(this.filteredRegistrations, 'season');

      // Convert to array of {season, registrations} objects, sorted by season desc
      return Object.entries(grouped)
        .map(([season, registrations]) => ({
          season,
          registrations,
          isExpanded: this.seasonExpanded[season]
        }))
        .sort((a, b) => b.season.localeCompare(a.season)); // Sort seasons in descending order
    },
    getCascaderOptionsForRegistration() {
      return (registration) => {
        const upgradeableMembershipList = _.get(registration, 'regoEntity.products.upgradeableMembershipList');
        return this.getFilterdMemberships(upgradeableMembershipList);
      };
    },
    upgradeDialogState() {
      if (!this.upgradeDialogData.registration) {
        return 'loading';
      }

      const options = this.getCascaderOptionsForRegistration(this.upgradeDialogData.registration);
      if (!options || options.length === 0) {
        return 'empty';
      }

      return 'ready';
    },
  },
  filters: {
    startCase,
    cap(value) {
      return _.capitalize(value);
    },
    formatDate(date) {
      return moment(date).tz(this.timezone).format('DD MMM YYYY, HH:mm');
    },
  }
};
</script>

<style lang="scss" scoped>
// Common styles
.w-100 {
  width: 100%;
}

.d-flex {
  display: flex;
}

.justify-content-between {
  justify-content: space-between;
}

.align-items-center {
  align-items: center;
}

.text-center {
  text-align: center;
}

.mb-05 {
  margin-bottom: 0.5rem;
}

.mb-10 {
  margin-bottom: 10px;
}

.mb-15 {
  margin-bottom: 15px;
}

.mb-20 {
  margin-bottom: 20px;
}

.mt-10 {
  margin-top: 10px;
}

.break-word {
  word-break: break-word;
}
::v-deep(.el-button+.el-button) {
  margin-left: 0;
}

// Card grid layout
.card-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
}

::v-deep(.el-card__header) {
  padding: 0;

  @media (max-width: 768px) {
    padding: 0 !important;
  }
}

::v-deep(.el-card__body) {
  @media (max-width: 768px) {
    padding: 10px !important;
  }
}

// Registration card styles
.registration-card {
  border-radius: 4px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03) !important;
  transition: all 0.2s ease;
  overflow: hidden;
  height: 100%;
  display: flex;
  flex-direction: column;
  margin-bottom: 0 !important;
}

.registration-card:hover {
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05) !important;
  }

  .card-header {
  padding: 14px 16px;
  border-bottom: 1px solid #EBEEF5;
  min-height: 56px; /* Set minimum height for consistent layout */
  display: flex;
  align-items: center;
}

.card-content {
  padding: 12px 0;
  background-color: #FFFFFF;
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
  margin-bottom: 0 !important;
}

.card-body-row {
  display: flex;
  flex-direction: column;
  border-bottom: 1px solid #f0f0f0;
  padding-bottom: 10px;
  margin-bottom: 10px;
}

.card-body-row:last-child {
  border-bottom: none;
  padding-bottom: 0;
  margin-bottom: 0;
}

.entity-name {
  font-size: 17px;
  font-weight: 600;
  color: #2c3e50;
  margin-right: 10px;
  white-space: normal;
  overflow: visible;
  text-overflow: initial;
  max-width: 90%;
  line-height: 1.3;
  word-wrap: break-word;
}

/* Mobile entity info container */
.entity-info-mobile {
  display: flex;
  flex-direction: column;
  flex: 1;
}

/* Mobile team code styling */
.team-code-mobile {
  margin-top: 4px;
}

.team-code-mobile-value {
  font-size: 13px;
  color: #909399;
  font-weight: normal;
}

.expand-button {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #606266;
  font-size: 13px;
  padding: 0;

  i {
    margin-left: 5px;
  }
}

.expanded-content {
  padding: 0 15px 15px;
}

// Info section for card content
.info-section {
  margin-bottom: 8px;
  display: flex;
  flex-direction: column;
}

.entity-name {
  font-size: 17px;
  font-weight: 600;
  color: #2c3e50;
  margin-right: 10px;
  white-space: normal;
  overflow: visible;
  text-overflow: initial;
  max-width: 90%;
  line-height: 1.3;
  word-wrap: break-word;
}

.entity-value, .season-value {
  font-weight: 500;
}

.info-col {
  flex: 1;
}

.dates-col {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
}

.date-row {
  display: flex;
  align-items: center;
}

.date-label {
  color: #909399;
  font-weight: 500;
  margin-right: 5px;
  font-size: 13px;
}

.date-value {
  color: #303133;
  font-size: 13px;
}

.mr-10 {
  margin-right: 10px;
}

// Action section
.action-section {
  margin-top: 0;
  padding-top: 5px;
  padding-bottom: 5px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.secondary-actions {
  margin-top: 5px;
  padding-top: 3px;
}

.action-buttons {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  align-items: center;
}

.payment-action-buttons {
  display: flex;
  gap: 8px;
  padding-right: 15px;
  margin-right: 15px;
  position: relative;
}

.expand-button {
  color: #606266;
  font-size: 13px;
  font-weight: 500;
  transition: all 0.2s;
  padding: 0;
  margin: 0;
  text-align: left;
}

.expand-button:hover {
  color: var(--primary-color, #409EFF);
}

.payment-action-buttons::after {
  content: '';
  position: absolute;
  right: 0;
  top: 5px;
  bottom: 5px;
  width: 1px;
  background-color: var(--border-color, #EBEEF5);
}

.action-buttons {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  position: relative;
}

.payment-actions {
  display: flex;
  gap: 5px;

  .el-button {
    padding: 5px 8px;
    font-size: 12px;
  }
}

.payment-status-container {
  display: flex;
  align-items: center;
  gap: 8px;
}

.payment-circle {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.amount-text {
  font-size: 10px;
  color: #909399;
  white-space: nowrap;
}

.payment-status-mobile {
  margin-right: 5px;
}

// Upgrade dropdown
.upgrade-dropdown {
  background-color: #f9f9f9;
  border-radius: 4px;
  padding: 10px;
}

// Info sections in expanded content
.info-section {
  margin-bottom: 24px;
  display: flex;
  flex-direction: column;
  background: white;
  border-radius: 10px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  border: 1px solid #f0f0f0;
}

.info-section h4 {
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 16px;
  color: #303133;
  border-bottom: 2px solid #409eff;
  padding-bottom: 8px;
  position: relative;
}

.info-section h4::before {
  content: "📝";
  margin-right: 8px;
  font-size: 14px;
}

.info-section .info-detail {
  display: grid;
  grid-template-columns: minmax(120px, 60%) 1fr;
  gap: 12px;
  margin-bottom: 12px;
  align-items: baseline;
  word-break: break-word;
  overflow-wrap: break-word;
  padding: 14px 16px;
  background-color: #fafbfc;
  border: 1px solid #e4e7ed;
  border-radius: 8px;
  transition: all 0.2s ease;
}

.info-section .info-detail:hover {
  border-color: #c0c4cc;
  background-color: #f5f7fa;
}

.info-section .info-detail strong {
  color: #606266;
  font-weight: 500;
}

.payment-info {
  font-size: 13px;
  margin-bottom: 5px;

  .payment-label {
    font-weight: 500;
    color: #909399;
    margin-right: 5px;
  }

  .payment-value {
    color: #303133;
  }
}

.file-group {
  margin-bottom: 15px;

  h5 {
    font-size: 13px;
    font-weight: 600;
    margin-bottom: 5px;
    color: #303133;
  }
}

// Transitions
.expand-enter-active,
.expand-leave-active {
  transition: all 0.3s ease;
  max-height: 1000px;
  overflow: hidden;
}

.expand-enter,
.expand-leave-to {
  max-height: 0;
  overflow: hidden;
}

// Info icon
.info-icon {
  color: #909399;
  font-size: 12px;
  margin-left: 3px;
  cursor: help;
}

// Filters container styling
.filters-container {
  background-color: #f9f9f9;
  padding: 15px;
  border-radius: 4px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);
  margin-bottom: 15px;
}

// Status details in expanded content
.status-details {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 5px;
}

.status-item {
  display: flex;
  align-items: center;
  gap: 10px;
}

  .status-label {
    font-weight: 500;
    color: #909399;
  min-width: 120px;
}

.ml-10 {
  margin-left: 10px;
}

.payment-section {
  margin-top: 10px;
}

.payment-row, .renewal-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.payment-info {
  display: flex;
  align-items: center;
}

.amount-text {
  font-size: 13px;
  font-weight: 500;
  color: #F56C6C;
  white-space: nowrap;
  margin-right: 10px;
}

// New styles for payment progress
.payment-progress {
  margin-right: 10px;
  min-width: 40px;
}

.payment-info-container {
  display: flex;
  align-items: center;
  margin-bottom: 5px;
}

// Box Card Mobile Styles
.box-card {
  border-radius: 4px !important;
  overflow: hidden;

  &.highlight-card {
    border-left-color: #E6A23C;
  }

  .header-container {
    background: linear-gradient(to right, #f6f8fa, white);
    padding: 0;
    border-bottom: 1px solid #eee;

    .d-flex {
      padding: 6px 10px;
    }
  }

  .entity-name {
    font-size: 15px;
    font-weight: 600;
    white-space: normal;
    overflow: visible;
    text-overflow: initial;
    max-width: 95%;
    line-height: 1.3;
    word-wrap: break-word;
    margin-right: 8px;
  }
}

.mobile-card-body {
  padding: 10px 12px;
}

.mobile-info-row {
  display: flex;
  align-items: center;
  margin-bottom: 6px;

  i {
    color: #909399;
    margin-right: 5px;
    font-size: 14px;
  }
}

.mobile-dates-container {
  margin-bottom: 6px;
}

.mobile-date-row {
  display: flex;
  align-items: center;
  margin-bottom: 4px;

  i {
    color: #909399;
    margin-right: 5px;
    font-size: 14px;
  }
}

.season-label {
  margin-left: auto;
  font-size: 13px;
  color: #606266;
}

.mobile-payment-row {
  margin-top: 6px;
  margin-bottom: 6px;
}

.mobile-renew-row {
  margin-top: 6px;
}

.amount-text {
  font-size: 13px;
  font-weight: 500;
  color: #F56C6C;
  margin-bottom: 5px;
}

.expand-arrow {
  text-align: center;
  padding: 5px 0;
  color: #909399;
  cursor: pointer;
  margin-top: 10px;
  border-top: 1px solid #f0f0f0;

  &:hover {
    color: #409EFF;
  }
}

.d-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin-top: 8px;
  padding: 0 12px 10px;
}

.grid-item {
  min-width: 0;
}

.grid-item i {
  margin-right: 4px;
  color: #909399;
}

.amount-text {
  font-size: 13px;
  font-weight: 500;
  color: #F56C6C;
  white-space: nowrap;
  margin-bottom: 5px;
  display: block;
}

.payment-section-mobile {
  margin-top: 5px;
  .el-button {
    padding: 7px 12px;
  }
}

.entity-type-tag, .season-tag {
  margin-right: 5px;
}

.tag-label {
  font-size: 12px;
  color: #909399;
  margin-right: 3px;
  margin-left: 4px;

  &:first-child {
    margin-left: 0;
  }
}

/* Desktop styles that need to be restored */
// Add styles for member type tag (desktop only) - no longer used
.member-type-tag {
  margin-left: 6px;
  font-weight: 500;
}

.info-label {
  color: #606266;
  font-weight: 500;
  font-size: 14px;
}

.info-value {
  color: #303133;
  font-size: 14px;
  margin-left: 5px;
}

.info-item {
  margin-bottom: 8px;
  display: flex;
  align-items: center;
}

/* Update styles to ensure padding is only for desktop */
.registration-card {
  /* Desktop specific card styles */
  .card-header {
    background-color: #fff;
    padding: 14px 18px; /* This padding is now explicitly only for desktop */
    border-bottom: 1px solid #eee;
  }
}

/* Mobile specific card styles */
.box-card {
  border-radius: 4px !important;
  overflow: hidden;

  &.highlight-card {
    border-left-color: #E6A23C;
  }

  .header-container {
    background: linear-gradient(to right, #f6f8fa, white);
    padding: 0; /* Zero padding for mobile */
    border-bottom: 1px solid #eee;

    .d-flex {
      padding: 6px 10px; /* Inner padding for content alignment */
    }
  }
}

.entity-tag {
  margin-bottom: 5px;
  background-color: #f0f2f5;
  color: #606266;
  border-color: #e4e7ed;
}

.pay-button {
  font-weight: 600;
  background-color: #F56C6C;
  border-color: #F56C6C;
  transition: all 0.2s ease;
  box-shadow: 0 1px 2px rgba(245, 108, 108, 0.15);
}

.pay-button:hover {
  background-color: #dd6161;
  border-color: #dd6161;
    transform: translateY(-1px);
  box-shadow: 0 2px 3px rgba(245, 108, 108, 0.2);
}

.renew-button {
  font-weight: 600;
  // background-color: #E6A23C;
  border-color: #E6A23C;
  transition: all 0.2s ease;
  box-shadow: 0 1px 2px rgba(230, 162, 60, 0.15);
}

.renew-button:hover {
  background-color: #d89614;
  border-color: #d89614;
    transform: translateY(-1px);
  box-shadow: 0 2px 3px rgba(230, 162, 60, 0.2);
}

.info-grid-three {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 0;
  width: 100%;
  padding: 12px 0 5px;
  position: relative;
}

.info-grid-three::before,
.info-grid-three::after {
  content: '';
  position: absolute;
  top: 15px;
  bottom: 15px;
  width: 1px;
  background-color: var(--border-color, #eaedf2);
}

.info-grid-three::before {
  left: 33.33%;
}

.info-grid-three::after {
  left: 66.66%;
}

.info-col {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0 5px;
}

.info-col .info-label {
  color: #909399;
  font-weight: 600;
  font-size: 13px;
  margin-bottom: 6px;
  display: flex;
  align-items: center;
}

.info-col .info-label i {
  margin-right: 5px;
  font-size: 14px;
}

.info-col .info-value {
  color: #2c3e50;
  font-size: 15px;
  font-weight: 500;
}

.info-col .member-type-tag {
  margin-top: 0;
  font-weight: 500;
  font-size: 13px;
  padding: 6px 12px;
}

.sort-container {
  padding: 0 10px;
}

.sort-wrapper {
  display: flex;
  align-items: center;
}

.sort-label {
  color: #606266;
  font-size: 14px;
  font-weight: 500;
}

.sort-select {
  width: 240px;
}

.entity-type-tag {
  font-weight: 600;
  text-transform: capitalize;
  padding: 4px 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: auto;
}

.entity-type-tag.el-tag--primary {
  background-color: var(--primary-color-lightest, #e6f2ff) !important;
  border-color: var(--primary-color-light, #80bdff) !important;
  color: var(--primary-color-dark, #0366d6) !important;
}

.entity-type-tag.el-tag--success {
  background-color: var(--success-color-lightest, #e6ffed) !important;
  border-color: var(--success-color-light, #85e89d) !important;
  color: var(--success-color-dark, #28a745) !important;
}

/* Status tags styling */
.el-tag--success.status-tag {
  background-color: var(--success-color-lightest, #f0f9eb) !important;
  border-color: var(--success-color-light, #c2e7b0) !important;
  color: var(--success-color, #67c23a) !important;
  font-weight: 600;
}

.el-tag--warning.status-tag {
  background-color: var(--warning-color-lightest, #fdf6ec) !important;
  border-color: var(--warning-color-light, #f5dab1) !important;
  color: var(--warning-color, #e6a23c) !important;
  font-weight: 600;
}

.el-tag--danger.status-tag {
  background-color: var(--danger-color-lightest, #fef0f0) !important;
  border-color: var(--danger-color-light, #fbc4c4) !important;
  color: var(--danger-color, #f56c6c) !important;
  font-weight: 600;
}

/* Improved action section */
.action-section {
  margin-top: 0;
  padding-top: 5px;
  padding-bottom: 5px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.expand-button {
  color: #606266;
  font-size: 13px;
  font-weight: 500;
  transition: all 0.2s;
  padding: 0;
  margin: 0;

  &:hover {
    color: var(--primary-color, #409EFF);
  }

  i {
    margin-left: 5px;
    transition: all 0.2s;
  }
}

.action-buttons .el-button {
  transition: all 0.2s;
}

.action-buttons .el-button:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
}

.pay-button, .renew-button {
  transform-origin: center;
}

.pay-button:hover, .renew-button:hover {
  transform: translateY(-1px) scale(1.05);
}

.pay-button:hover {
  background-color: #dd6161 !important;
  border-color: #dd6161 !important;
}

.renew-button:hover {
  background-color: #d89614 !important;
  border-color: #d89614 !important;
}

.member-type-tag {
  margin-top: 0;
  font-weight: 500;
  font-size: 13px;
  padding: 6px 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: auto;
}

/* Dynamic color branding */
.registration-card {
  /* Default blue branding */
  &.active-card .card-header {
    border-bottom-color: var(--success-color-light, rgba(103, 194, 58, 0.2));
    background: linear-gradient(to right, var(--success-color-lighter, rgba(103, 194, 58, 0.05)), transparent);
  }

  &.pending-card .card-header {
    border-bottom-color: var(--warning-color-light, rgba(230, 162, 60, 0.2));
    background: linear-gradient(to right, var(--warning-color-lighter, rgba(230, 162, 60, 0.05)), transparent);
  }

  &.declined-card .card-header {
    border-bottom-color: var(--danger-color-light, rgba(245, 108, 108, 0.2));
    background: linear-gradient(to right, var(--danger-color-lighter, rgba(245, 108, 108, 0.05)), transparent);
  }

  &.expired-card .card-header {
    border-bottom-color: var(--info-color-light, rgba(144, 147, 153, 0.2));
    background: linear-gradient(to right, var(--info-color-lighter, rgba(144, 147, 153, 0.05)), transparent);
  }
}

/* Entity type specific styling */
.registration-card[data-entity-type="membership"] {
  .info-grid-three::before,
  .info-grid-three::after {
    background-color: var(--primary-color-lighter, rgba(3, 102, 214, 0.1));
  }

  .action-section {
    border-top-color: var(--primary-color-lighter, rgba(3, 102, 214, 0.1));
  }
}

.registration-card[data-entity-type="event"] {
  .info-grid-three::before,
  .info-grid-three::after {
    background-color: var(--success-color-lighter, rgba(40, 167, 69, 0.1));
  }

  .action-section {
    border-top-color: var(--success-color-lighter, rgba(40, 167, 69, 0.1));
  }
}

// Season grouping styles
.season-group {
  margin-bottom: 25px;
}

.season-header {
  background-color: #F2F6FC;
  border-radius: 4px;
  padding: 12px 15px;
  margin-bottom: 15px;
  cursor: pointer;
  transition: all 0.2s;
  border: 1px solid #EBEEF5;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.02);
}

.season-header:hover {
  background-color: #E6F1FC;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.03);
}

.season-title {
  font-size: 15px;
  font-weight: 600;
  color: #303133;
  display: flex;
  align-items: center;
}

.season-title i {
  margin-right: 8px;
  transition: transform 0.2s;
}

.season-count {
  font-size: 13px;
  color: #909399;
}

.season-content {
  padding: 0 10px;
  animation: fadeIn 0.3s ease-in-out;
  padding-bottom: 0 !important;
  margin-bottom: 0 !important;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(-10px); }
  to { opacity: 1; transform: translateY(0); }
}

// Member type styling (replaced tag style)
.member-type-value {
  font-size: 15px;
  font-weight: 600;
  color: var(--primary-color, #409EFF);
  margin-top: 4px;
  text-transform: capitalize;
}

.member-type-value-mobile {
  font-weight: 600;
  color: var(--primary-color, #409EFF);
}

.member-type-value-inline {
  font-weight: 600;
  color: var(--primary-color, #409EFF);
}

// Add styling for entity type
.entity-type {
  font-weight: 600;
  margin-right: 10px;
  text-transform: capitalize;
}

.entity-type-mobile {
  font-weight: 600;
  text-transform: capitalize;
}

.season-info {
  border-top: 1px solid var(--border-color, #eaedf2);
  padding-top: 10px;
  margin-top: 10px;
}

/* Updated unified info grid styles for 1x3 layout */
.unified-info-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0;
  width: 100%;
  padding: 16px 12px 25px;
  background-color: #FAFBFC;
  border-radius: 4px;
  margin-bottom: 0;
  min-height: 80px; /* Ensure consistent height */
  position: relative; /* Add position relative for absolute positioning of arrow */
}

.unified-info-grid .info-col {
  position: relative;
  padding: 0 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.unified-info-grid .info-col:not(:last-child)::after {
  content: '';
  position: absolute;
  top: 5px;
  bottom: 5px;
  right: 0;
  width: 1px;
  background-color: #eaedf2;
}

/* Entity type tag in header */
.entity-type-tag {
  font-size: 12px;
  padding: 2px 8px;
  height: 22px;
  line-height: 18px;
  margin-left: 8px;
  text-transform: capitalize;
}

.ml-5 {
  margin-left: 5px;
}

/* Special value colors */
.entity-value {
  color: var(--primary-color, #409EFF);
}

.season-value {
  color: var(--success-color, #67C23A);
}

/* Entity name styling in list view */
.entity-name-value {
  font-weight: normal;
  font-size: 14px;
  color: #2c3e50;
  white-space: normal;
  overflow: visible;
  text-overflow: initial;
  max-width: none;
  display: block;
  word-break: break-word;
  word-wrap: break-word;
  cursor: default;
}

/* Team code styling in desktop table */
.entity-display {
  display: flex;
  flex-direction: column;
}

.team-code-row {
  margin-top: 4px;
}

.team-code-value {
  font-size: 12px;
  color: #909399;
  font-weight: normal;
}

/* Payment and renew button styling in list view */
.pay-button-list {
  font-weight: 600;
  background-color: var(--danger-color, #F56C6C);
  border-color: var(--danger-color, #F56C6C);
  transition: all 0.2s ease;
  white-space: nowrap;
  box-shadow: 0 2px 4px rgba(245, 108, 108, 0.3);
}

.pay-button-list:hover {
  background-color: var(--danger-color-hover, #dd6161);
  border-color: var(--danger-color-hover, #dd6161);
  transform: translateY(-1px);
  box-shadow: 0 3px 6px rgba(245, 108, 108, 0.4);
}

.renew-button-list {
  font-weight: 600;
  background-color: var(--warning-color, #E6A23C);
  border-color: var(--warning-color, #E6A23C);
  transition: all 0.2s ease;
  white-space: nowrap;
  box-shadow: 0 2px 4px rgba(230, 162, 60, 0.3);
}

.renew-button-list:hover {
  background-color: var(--warning-color-hover, #d89614);
  border-color: var(--warning-color-hover, #d89614);
  transform: translateY(-1px);
  box-shadow: 0 3px 6px rgba(230, 162, 60, 0.4);
}

.renew-button-list:disabled {
  background-color: #F5F7FA;
  border-color: #E4E7ED;
  color: #C0C4CC;
  box-shadow: none;
}

.view-toggle {
  display: flex;
  align-items: center;
  margin-right: 10px;
}

.view-toggle .el-radio-group {
  display: flex;
  align-items: center;
}

.view-toggle .el-radio-button {
  margin-right: 10px;
}

.view-toggle .el-radio-button__inner {
  display: flex;
  align-items: center;
  padding: 8px 15px;
}

.view-toggle .el-radio-button__inner i {
  margin-right: 5px;
}

/* Add style for payment actions container */
.payment-actions-container {
  display: flex;
  margin-top: 5px;
  gap: 5px;
}

/* Improve entity cell styling */
.entity-cell {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 5px;
}

.entity-name-value {
  font-weight: normal;
  font-size: 14px;
  color: #2c3e50;
  white-space: normal;
  overflow: visible;
  text-overflow: initial;
  max-width: none;
  display: block;
  word-break: break-word;
  word-wrap: break-word;
}

.payment-actions-container {
  display: flex;
  gap: 5px;
}

/* Enhanced table styling */
::v-deep(.list-view .el-table) {
  border-radius: 4px;
  overflow: hidden;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
  border: 1px solid #ebeef5;
  width: 100% !important;
  table-layout: fixed !important;
}

/* Make sure headers align with content */
::v-deep(.clean-table .el-table__header-wrapper) {
  background-color: #F5F7FA;
}

/* Remove excess CSS */
::v-deep(.list-view .el-table--border td, .list-view .el-table--border th),
::v-deep(.list-view .el-table--border),
::v-deep(.list-view .el-table .el-table__row td:last-child),
::v-deep(.el-table th > .cell),
::v-deep(.el-table__header),
::v-deep(.el-table .cell),
::v-deep(.list-view .el-table th:nth-child(3) .cell),
::v-deep(.list-view .el-table th:nth-child(4) .cell) {
  /* Reset to default Element UI styling */
}

/* Fix the last column (actions) to have a border */
::v-deep(.list-view .el-table .el-table__row td:last-child) {
  border-right: 1px solid #ebeef5;
}

/* Make the table header stick to the top when scrolling */
::v-deep(.list-view .el-table__header-wrapper) {
  background-color: #f5f7fa;
}

/* Cell content styling improvements */
.date-value {
  font-size: 13px;
  white-space: nowrap;
  text-align: center;
  display: block;
}

.entity-value {
  text-align: center;
  display: block;
}

/* Fix Member Type and Entity Type column alignment */
::v-deep(.list-view .el-table .el-table_1_column_2 .cell),
::v-deep(.list-view .el-table .el-table_1_column_3 .cell) {
  text-align: center;
}

/* Ensure list values are centrally aligned */
.list-value {
  font-size: 14px;
  color: #303133;
  padding: 0 5px;
  word-break: break-word;
  word-wrap: break-word;
  white-space: normal;
  text-align: center;
  display: block;
}

/* Entity cell padding */
.entity-cell {
  padding: 0 5px;
}

/* Entity name value improvements */
.entity-name-value {
  font-weight: normal;
  font-size: 14px;
  color: #2c3e50;
  white-space: normal;
  overflow: visible;
  text-overflow: initial;
  max-width: none;
  display: block;
  word-break: break-word;
  word-wrap: break-word;
  cursor: default;
  line-height: 1.4;
}

.action-icon {
  font-size: 20px;
  color: #909399;
  cursor: pointer;
  padding: 5px;
  border-radius: 50%;
  transition: all 0.2s;
}

.action-icon:hover {
  color: #409EFF;
  background-color: #ecf5ff;
}

/* Action icon and dropdown styling */
.list-actions {
  display: flex;
  justify-content: center;
  align-items: center;
}

.action-icon {
  font-size: 20px;
  color: #909399;
  cursor: pointer;
  padding: 8px;
  border-radius: 50%;
  transition: all 0.2s;
  display: inline-flex;
}

.action-icon:hover {
  color: #409EFF;
  background-color: #ecf5ff;
}

/* Fix expanded content positioning */
.list-expanded-content {
  position: relative;
  left: -300px;
  width: 500px;
  margin: 10px 15px 15px;
  border-radius: 6px;
  background-color: #f9fafc;
  border: 1px solid #eaedf2;
  z-index: 10;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.entityCreatedFirstLevelLimited {
  white-space: normal;
  overflow: visible;
  text-overflow: initial;
  max-width: none;
}

.entityCreatedSecondLevelLimited {
  white-space: normal;
  overflow: visible;
  text-overflow: initial;
  max-width: none;
}

/* Add specific styles for the table headers */
::v-deep(.el-table__header) {
  table-layout: fixed;
}

/* Ensure table cells don't expand beyond their allocated width */
::v-deep(.el-table .cell) {
  padding: 8px;
  white-space: normal;
  word-break: break-word;
  overflow: hidden;
  box-sizing: border-box;
  width: 100%;
}

/* Force table layout to fixed */
::v-deep(.list-view .el-table) {
  table-layout: fixed !important;
  width: 100% !important;
}

/* Ensure Member Type column displays correctly */
::v-deep(.el-table th:nth-child(3) .cell) {
  white-space: normal;
  word-break: break-word;
}

/* Ensure Registration date column displays correctly */
::v-deep(.el-table th:nth-child(4) .cell) {
  white-space: normal;
  word-break: break-word;
}

/* Date value specific styling */
.date-value {
  font-size: 13px;
  white-space: nowrap;
  text-align: center;
  display: block;
}

/* Make sure date columns are properly sized */
::v-deep(.el-table .el-table_1_column_4),
::v-deep(.el-table .el-table_1_column_5) {
  width: 120px !important;
}

/* Give the table some breathing room */
::v-deep(.list-view .el-table__body) {
  width: 100% !important;
}

/* Ensure consistent column sizing */
::v-deep(.el-table__header-wrapper),
::v-deep(.el-table__body-wrapper) {
  width: 100% !important;
}

/* Entity cell styling */
.entity-cell {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
  padding: 0 5px;
}

/* Status tags enhanced styling */
.status-tag {
  display: inline-flex;
  justify-content: center;
  align-items: center;
  height: 24px;
  line-height: 22px;
  padding: 0 10px;
  font-size: 12px;
  white-space: nowrap;
  border-radius: 4px;
}

/* Row hover effect */
::v-deep(.list-view .el-table__row:hover td) {
  background-color: #f0f7ff !important;
}

/* Fix dropdown menu show-more-item */
::v-deep(.show-more-item) {
  display: flex !important;
  align-items: center !important;
}

/* Action buttons styling */
.list-actions {
  display: flex;
  justify-content: center;
}

.list-actions .el-dropdown button {
  padding: 8px 15px;
}

/* Enhanced list view season container */
.list-view .season-group {
  margin-bottom: 30px;
}

.list-view .season-header {
  background-color: #f0f7ff;
  border-radius: 8px 8px 0 0;
  padding: 14px 18px;
  margin-bottom: 0;
  border-bottom: none;
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.05);
  position: relative;
  z-index: 2;
}

.list-view .list-content {
  border-radius: 0 0 8px 8px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
  overflow: hidden;
}

/* Fix expanded section with proper styling */
.list-expanded-content {
  margin: 0 0 15px;
  border-radius: 4px;
  background-color: #FFFFFF;
  border: 1px solid #EBEEF5;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
}

/* Add a transition for smoother experience */
.list-view .el-table__row {
  transition: all 0.2s ease;
}

/* Make sure the entity name is left-aligned */
.entity-name-value {
  text-align: left;
}

/* Ensure consistent column sizing in the table */
::v-deep(.list-view .el-table .cell) {
  box-sizing: border-box;
  overflow: hidden;
}

/* Fix the entity column specifically */
::v-deep(.list-view .el-table .el-table_1_column_1 .cell) {
  text-align: left;
  padding-left: 10px;
}

/* Override the centered cell style for entity column */
::v-deep(.list-view .el-table .el-table_1_column_1) {
  text-align: left !important;
}

::v-deep(.list-view .el-table .el-table_1_column_1 .cell) {
  text-align: left !important;
  padding-left: 10px;
  word-break: break-word;
}

/* Entity name value improvements */
.entity-name-value {
  font-weight: normal;
  font-size: 14px;
  color: #2c3e50;
  white-space: normal;
  overflow: visible;
  text-overflow: initial;
  max-width: none;
  display: block;
  word-break: break-word;
  word-wrap: break-word;
  cursor: default;
  line-height: 1.4;
  text-align: left !important;
}

/* Entity cell styling */
.entity-cell {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
  padding: 0 5px;
  text-align: left !important;
}

/* Make sure date columns are properly sized */
::v-deep(.el-table .el-table_1_column_4 .cell),
::v-deep(.el-table .el-table_1_column_5 .cell) {
  padding: 0 2px;
  white-space: nowrap;
}

/* Fix header text wrapping */
::v-deep(.el-table .cell) {
  white-space: normal;
  word-break: break-word;
  overflow: hidden;
  box-sizing: border-box;
  width: 100%;
}

::v-deep(.el-table th > .cell) {
  white-space: nowrap !important;
  padding: 0 4px;
}

/* Ensure the Type column has enough space for "Membership" */
.entity-value {
  white-space: nowrap;
  display: inline-block;
  text-align: center;
  width: 100%;
}

/* Fix cell alignment for entity column */
::v-deep(.el-table th[class*="el-table_1_column_1"]) {
  text-align: left !important;
  padding-left: 10px;
}

/* Ensure the entity cell columns are properly aligned */
.entity-cell {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
  padding: 0 5px;
  text-align: left !important;
}

/* Ensure consistent text alignment */
::v-deep(.el-table--border th.is-leaf) {
  border-right: 1px solid #ebeef5;
}

/* Table layout fixes */
::v-deep(.list-view .el-table) {
  width: 100% !important;
  table-layout: fixed !important;
  border-collapse: collapse;
}

/* Make header align with data cells */
::v-deep(.el-table__header-wrapper) {
  overflow: hidden;
}

/* Fix the actions column header */
::v-deep(.actions-column .cell) {
  padding: 0;
}

::v-deep(.el-table__body td:last-child),
::v-deep(.el-table__header th:last-child) {
  border-right: 1px solid #ebeef5;
}

/* Updated expanded content styles for list view */
.list-expanded-content {
  margin: 0 0 15px;
  border-radius: 4px;
  background-color: #FFFFFF;
  border: 1px solid #EBEEF5;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
  position: relative;
  z-index: 10;
  overflow: hidden;
}

/* Clean up the table styling to look more professional */
.clean-table {
  border: 1px solid #EBEEF5 !important;
  border-radius: 4px !important;
  overflow: hidden;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03) !important;
}

/* Style the table headers to be cleaner */
::v-deep(.clean-table th) {
  background-color: #F5F7FA !important;
  font-weight: 500 !important;
  color: #606266 !important;
  border-bottom: 1px solid #EBEEF5 !important;
  padding: 10px 0 !important;
}

/* Improve the cell styling */
::v-deep(.clean-table td) {
  border-bottom: 1px solid #EBEEF5 !important;
  padding: 14px 0 !important;
}

/* Make the borders lighter */
::v-deep(.clean-table td, .clean-table th) {
  border-right: 1px solid #EBEEF5 !important;
}

/* Adjust borders for the last cell */
::v-deep(.clean-table td:last-child, .clean-table th:last-child) {
  border-right: none !important;
}

/* Subtle row hover */
::v-deep(.clean-table tr:hover > td) {
  background-color: #F5F7FA !important;
}

/* Simplify tag styling */
.status-tag {
  font-weight: 500;
  border-radius: 4px;
  padding: 4px 8px;
  font-size: 12px;
}

/* Make entity cells cleaner */
.entity-cell {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  padding: 0 12px;
  text-align: left !important;
}

/* Improved entity name styling */
.entity-name-value {
  font-weight: normal;
  font-size: 14px;
  color: #303133;
  white-space: normal;
  overflow: visible;
  text-overflow: initial;
  word-break: break-word;
  line-height: 1.4;
  text-align: left !important;
}

/* Style the action icons */
.action-icon {
  color: #909399;
  cursor: pointer;
  font-size: 18px;
  transition: color 0.2s;
}

.action-icon:hover {
  color: #409EFF;
}

/* Clean up expanded content */
.list-expanded-content {
  margin: 0 0 15px;
  border-radius: 4px;
  background-color: #FFFFFF;
  border: 1px solid #EBEEF5;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
}

/* Season header styling */
.season-header {
  background-color: #F2F6FC;
  border-radius: 4px;
  padding: 12px 15px;
  margin-bottom: 15px;
  cursor: pointer;
  transition: all 0.2s;
  border: 1px solid #EBEEF5;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.02);
}

.season-header:hover {
  background-color: #E6F1FC;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.03);
}

.season-title {
  font-size: 15px;
  font-weight: 600;
  color: #303133;
  display: flex;
  align-items: center;
}

.season-count {
  font-size: 13px;
  color: #909399;
}

/* Simplified list values */
.list-value {
  font-size: 14px;
  color: #303133;
  word-break: break-word;
  text-align: center;
  display: block;
}

/* Clean up date format */
.date-value {
  font-size: 13px;
  text-align: center;
  display: block;
}

/* Word breaking class */
.break-word {
  word-break: break-word;
  white-space: normal;
}

/* Make the tabs cleaner in expanded content */
::v-deep(.list-expanded-tabs .el-tabs__header) {
  background-color: #F5F7FA;
  margin: 0;
}

::v-deep(.list-expanded-tabs .el-tabs__item) {
  height: 40px;
  line-height: 40px;
}

/* Fix the last column (actions) and ensure proper alignment */
::v-deep(.actions-column .cell) {
  text-align: center;
}

/* Simplify the season header to be more subtle */
.season-header {
  background-color: #F2F6FC;
  border-radius: 4px;
  padding: 12px 15px;
  margin-bottom: 12px;
  cursor: pointer;
  border: 1px solid #EBEEF5;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.02);
}

.season-header:hover {
  background-color: #E6F1FC;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.03);
}

/* Enhanced entity name display */
.entity-name-value {
  font-size: 14px;
  color: #303133;
  line-height: 1.4;
  text-align: left;
  word-break: break-word;
}

/* Status tag styling to match the portal design */
.status-tag, ::v-deep(.el-tag--mini) {
  display: inline-flex;
  justify-content: center;
  align-items: center;
  height: 24px;
  line-height: 22px;
  padding: 0 10px;
  font-size: 12px;
  white-space: nowrap;
  border-radius: 4px;
  text-transform: capitalize;
  font-weight: normal;
  max-width: 110px;
}

/* Apply a consistent border style across the component */
.clean-table, .season-header, .registration-card, .list-expanded-content, .unified-info-grid {
  border-color: #EBEEF5;
}

/* Ensure consistent shadows */
.clean-table, .registration-card, .list-expanded-content {
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
}

/* Consistent tag styling */
::v-deep(.el-tag--success) {
  background-color: #F0F9EB;
  border-color: #E1F3D8;
  color: #67C23A;
}

::v-deep(.el-tag--warning) {
  background-color: #FDF6EC;
  border-color: #FAF0E6;
  color: #E6A23C;
}

::v-deep(.el-tag--danger) {
  background-color: #FEF0F0;
  border-color: #FDE2E2;
  color: #F56C6C;
}

::v-deep(.el-tag--info) {
  background-color: #F4F4F5;
  border-color: #E9E9EB;
  color: #909399;
}

/* Add slightly rounded corners to table cells */
.list-value, .entity-name-value, .date-value {
  border-radius: 2px;
}

/* Consistent row height and spacing */
::v-deep(.clean-table .el-table__row) {
  height: 50px;
}

/* Fix dividers between cells */
::v-deep(.clean-table th, .clean-table td) {
  border-bottom-color: #EBEEF5;
  border-right-color: #EBEEF5;
}

/* Fix table borders */
::v-deep(.clean-table) {
  border: 1px solid #EBEEF5;
}

/* Add smooth transitions */
.entity-name-value, .list-value, .date-value, .action-icon {
  transition: all 0.2s;
}

/* Ensure specific color schemes for different tag types */
::v-deep(.el-tag--success.el-tag--plain) {
  background-color: #F0F9EB;
  border-color: #E1F3D8;
  color: #67C23A;
}

::v-deep(.el-tag--warning.el-tag--plain) {
  background-color: #FDF6EC;
  border-color: #FAF0E6;
  color: #E6A23C;
}

::v-deep(.el-tag--danger.el-tag--plain) {
  background-color: #FEF0F0;
  border-color: #FDE2E2;
  color: #F56C6C;
}

::v-deep(.el-tag--info.el-tag--plain) {
  background-color: #F4F4F5;
  border-color: #E9E9EB;
  color: #909399;
}

/* Ensure specific color schemes for different tag types with plain effect */
::v-deep(.el-tag--success.el-tag--plain) {
  background-color: #F0F9EB;
  border-color: #E1F3D8;
  color: #67C23A;
}

::v-deep(.el-tag--warning.el-tag--plain) {
  background-color: #FDF6EC;
  border-color: #FAF0E6;
  color: #E6A23C;
}

::v-deep(.el-tag--danger.el-tag--plain) {
  background-color: #FEF0F0;
  border-color: #FDE2E2;
  color: #F56C6C;
}

::v-deep(.el-tag--info.el-tag--plain) {
  background-color: #F4F4F5;
  border-color: #E9E9EB;
  color: #909399;
}

/* Fix the "Deregistered" special styling */
::v-deep(.el-tag--danger) {
  background-color: #FEF0F0;
  border-color: #FDE2E2;
  color: #F56C6C;
}

.action-buttons {
  display: flex;
  gap: 0;
  flex-wrap: nowrap;
  align-items: center;
}

.payment-action-buttons {
  display: flex;
  gap: 5px;
  padding-right: 10px;
  margin-right: 10px;
  position: relative;
}

.payment-action-buttons::after {
  content: '';
  position: absolute;
  right: 0;
  top: 2px;
  bottom: 2px;
  width: 1px;
  background-color: #EBEEF5;
}

.action-section {
  margin-top: 0;
  padding-top: 5px;
  padding-bottom: 5px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.ml-5 {
  margin-left: 5px;
}

/* Improve button group appearance */
.el-button-group {
  margin-right: 0;
}

.el-button-group .el-button--mini {
  padding: 5px 9px;
  line-height: 1.2;
  border-radius: 3px;
}

/* Make icons a bit smaller in mini buttons */
.el-button--mini [class*=" el-icon-"],
.el-button--mini [class^="el-icon-"] {
  font-size: 12px;
  margin-right: 2px;
}

.secondary-actions {
  margin-top: 5px;
}

.action-buttons {
  display: flex;
  gap: 5px;
  flex-wrap: nowrap;
  align-items: center;
}

.payment-action-buttons {
  display: flex;
  gap: 5px;
  padding-right: 10px;
  margin-right: 10px;
  position: relative;
}

.payment-action-buttons::after {
  content: '';
  position: absolute;
  right: 0;
  top: 2px;
  bottom: 2px;
  width: 1px;
  background-color: #EBEEF5;
}

.action-section {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  flex-wrap: wrap;
  gap: 5px;
}

.mb-10 {
  margin-bottom: 10px;
}

.ml-5 {
  margin-left: 5px;
}

.expand-button-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #606266;
  font-size: 16px;
  padding: 0;
  margin: 0;
  height: 32px;
  width: 32px;
  border-radius: 50%;
  transition: all 0.2s;
}

.expand-button-icon:hover {
  background-color: #f5f7fa;
  color: var(--primary-color, #409EFF);
}

.arrow-icon {
  transition: transform 0.3s ease;
}

.expand-button-icon:hover .arrow-icon {
  transform: scale(1.2);
}

/* Unified info grid styles with position relative for the arrow button */
.unified-info-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px 0;
  width: 100%;
  padding: 10px 0px 25px;
  background-color: #FAFBFC;
  border-radius: 4px;
  margin-bottom: 0;
  min-height: 80px; /* Ensure consistent height */
  position: relative; /* Add position relative for absolute positioning of arrow */
}

/* Position the expand button container at the bottom of the unified grid */
.expand-button-container {
  position: absolute;
  bottom: -16px;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  z-index: 2;
}

.expand-button-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #606266;
  font-size: 16px;
  padding: 0;
  margin: 0;
  height: 32px;
  width: 32px;
  background-color: white;
  border-radius: 50%;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  transition: all 0.2s;
  border: 1px solid #EBEEF5;
}

.card-grid .registration-card:last-child {
  margin-bottom: 0 !important;
}

/* Entity type text above entity name */
.entity-type-text {
  font-size: 13px;
  color: #606266;
  text-transform: capitalize;
  font-weight: 500;
  margin-bottom: 4px;
}

.d-flex.flex-column {
  display: flex;
  flex-direction: column;
}

/* Expanded row styling */
.table-expand-content {
  padding: 10px 20px;
  background-color: #FAFBFC;
}

.expanded-tabs {
  margin-top: 10px;
  border-radius: 4px;
  overflow: hidden;
}

::v-deep(.el-table__expanded-cell) {
  padding: 15px 50px !important;
  background-color: #FAFBFC;
}

::v-deep(.el-table__expand-icon) {
  transition: transform 0.2s ease;
  margin-right: 5px;

  &.el-table__expand-icon--expanded {
    transform: rotate(90deg);
  }

  .el-icon {
    font-weight: bold;
    font-size: 14px;
    color: #409EFF;
  }
}

/* Add some spacing around the expanded content tabs */
::v-deep(.expanded-tabs .el-tabs__header) {
  background-color: #F5F7FA;
  margin: 0;
}

::v-deep .el-select-dropdown__item:last-child {
  margin-bottom: 5px; /* Add a bit of bottom margin to the last item */
}

// Google Places Autocomplete z-index fix for dialogs
::v-deep .pac-container {
  z-index: 999999 !important;
  position: absolute !important;
}

// Ensure renew payment dialog has proper z-index context
::v-deep .renew-payment-dialog {
  z-index: 9999 !important;

  .el-dialog__wrapper {
    z-index: 9999 !important;
  }
}

::v-deep(.expanded-tabs .el-tabs__item) {
  height: 40px;
  line-height: 40px;
}

/* Expanded row styling */
.table-expand-content {
  padding: 10px 20px;
  background-color: #FAFBFC;
}

.expanded-tabs {
  margin-top: 10px;
  border-radius: 4px;
  overflow: hidden;
  box-shadow: none;
}

.expanded-details {
  padding: 15px;
}

.info-section {
  margin-bottom: 20px;
  border-bottom: 1px solid #EBEEF5;
  padding-bottom: 15px;
}

.info-section:last-child {
  border-bottom: none;
  padding-bottom: 0;
}

.info-section h4 {
  margin-top: 0;
  margin-bottom: 12px;
  font-size: 16px;
  font-weight: 600;
  color: #303133;
}

.info-detail {
  margin-bottom: 8px;
  display: flex;
  align-items: flex-start;
}

.info-detail strong {
  min-width: 120px;
  display: inline-block;
  font-weight: 500;
  color: #606266;
}

.expanded-actions {
  margin-top: 15px;
  display: flex;
  gap: 10px;
  border-top: 1px solid #EBEEF5;
  padding-top: 15px;
}

::v-deep(.el-table__expanded-cell) {
  padding: 15px 50px !important;
  background-color: #FAFBFC;
}

::v-deep(.el-table__expand-icon) {
  transition: transform 0.2s ease;
  margin-right: 5px;

  &.el-table__expand-icon--expanded {
    transform: rotate(90deg);
  }

  .el-icon {
    font-weight: bold;
    font-size: 14px;
    color: #409EFF;
  }
}

/* Add some spacing around the expanded content tabs */
::v-deep(.expanded-tabs .el-tabs__header) {
  background-color: #F5F7FA;
  margin: 0;
}

::v-deep(.expanded-tabs .el-tabs__item) {
  height: 40px;
  line-height: 40px;
}

.status-details {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.status-item {
  display: flex;
  align-items: center;
  gap: 10px;
}

.status-label {
  min-width: 120px;
  font-weight: 500;
  color: #909399;
}

.payment-info {
  margin-bottom: 8px;
}

.payment-label {
  font-weight: 500;
  color: #909399;
  min-width: 120px;
  display: inline-block;
}

.payment-value {
  color: #303133;
}

.upgrade-dropdown {
  margin-top: 10px;
  padding: 10px;
  background-color: #f9f9f9;
  border-radius: 4px;
  border: 1px solid #EBEEF5;
}

// ... rest of existing styles ...

/* Upgrade Dialog Styling */
.upgrade-membership-dialog {
  .el-dialog__header {
    background: linear-gradient(135deg, #409EFF 0%, #67C23A 100%);
    color: white;
    padding: 20px 24px;
    border-radius: 8px 8px 0 0;

    .el-dialog__title {
      color: white;
      font-weight: 600;
      font-size: 18px;
    }

    .el-dialog__close {
      color: white;
      font-size: 20px;

      &:hover {
        color: rgba(255, 255, 255, 0.8);
      }
    }
  }

  .el-dialog__body {
    padding: 24px;
    background-color: #fafbfc;
  }

  .upgrade-dialog-content {
    background: white;
    border-radius: 8px;
    padding: 20px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    width: 100%;
  }

  .upgrade-instruction {
    color: #606266;
    font-size: 14px;
    margin-bottom: 16px;
    display: flex;
    align-items: center;

    &::before {
      content: "🔄";
      margin-right: 8px;
      font-size: 16px;
    }
  }

  .upgrade-cascader {
    .el-cascader {
      width: 100%;

      .el-input__inner {
        height: 44px;
        border-radius: 8px;
        border: 2px solid #DCDFE6;
        font-size: 14px;
        transition: all 0.3s ease;

        &:focus {
          border-color: #409EFF;
          box-shadow: 0 0 0 3px rgba(64, 158, 255, 0.1);
        }
      }

      .el-input__suffix {
        right: 15px;
      }
    }
  }

  .el-dialog__footer {
    padding: 16px 24px;
    background-color: #f5f7fa;
    border-radius: 0 0 8px 8px;
    display: flex;
    justify-content: flex-end;
    gap: 12px;

    .el-button {
      padding: 10px 20px;
      border-radius: 6px;
      font-weight: 500;

      &:not(.el-button--primary) {
        background: white;
        border-color: #DCDFE6;
        color: #606266;

        &:hover {
          border-color: #409EFF;
          color: #409EFF;
        }
      }
    }
  }
}

/* Cascader Dropdown Panel Styling */
::v-deep(.upgrade-membership-dialog .el-cascader-panel) {
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);

  .el-cascader-menu {
    border-right: 1px solid #f0f0f0;

    &:last-child {
      border-right: none;
    }
  }

  .el-cascader-node {
    padding: 12px 16px;
    transition: all 0.2s ease;

    &:hover {
      background-color: #f5f7fa;
    }

    &.is-active {
      background-color: #409EFF;
      color: white;

      .membership-option-content {
        .membership-name {
          color: white;
        }

        .el-tag {
          background-color: rgba(255, 255, 255, 0.2);
          border-color: rgba(255, 255, 255, 0.3);
          color: white;
        }
      }
    }

    .el-cascader-node__label {
      padding: 0;
    }
  }
}

.membership-option-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;

  .membership-name {
    font-weight: 500;
    color: #303133;
    font-size: 14px;
  }

  .membership-details {
    display: flex;
    align-items: center;
    gap: 8px;

    .membership-type {
      color: #909399;
      font-size: 13px;
    }

    .el-tag {
      font-size: 11px;
      padding: 2px 8px;
      border-radius: 12px;
      font-weight: 500;
    }
  }
}

.upgrade-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 40px 20px;
  color: #909399;

  .el-icon-loading {
    font-size: 32px;
    margin-bottom: 12px;
    color: #409EFF;
  }
}

.upgrade-empty-state {
  text-align: center;
  padding: 40px 20px;
  color: #909399;

  .el-icon-box {
    font-size: 48px;
    color: #C0C4CC;
    margin-bottom: 16px;
  }

  .empty-title {
    font-size: 16px;
    color: #606266;
    margin-bottom: 8px;
  }

  .empty-description {
    font-size: 14px;
    line-height: 1.5;
  }
}

/* Responsive Design for Upgrade Dialog */
@media (max-width: 768px) {
  .upgrade-membership-dialog {
    .el-dialog {
      width: 95% !important;
      margin: 0 2.5% !important;
    }

    .el-dialog__header {
      padding: 16px 20px;

      .el-dialog__title {
        font-size: 16px;
      }
    }

    .el-dialog__body {
      padding: 16px;
    }

    .upgrade-dialog-content {
      padding: 16px;
    }

    .upgrade-cascader .el-cascader .el-input__inner {
      height: 40px;
      font-size: 16px; /* Prevent zoom on iOS */
    }

    .upgrade-instruction {
      font-size: 14px;
      margin-bottom: 12px;
    }

    .el-dialog__footer {
      padding: 12px 20px;
      flex-direction: column-reverse;
      gap: 8px;

      .el-button {
        width: 100%;
        margin: 0;
      }
    }
  }
}

@media (max-width: 480px) {
  .upgrade-membership-dialog {
    .el-dialog {
      width: 100% !important;
      margin: 0 !important;
      border-radius: 0;
    }

    .el-dialog__header {
      border-radius: 0;
    }

    .upgrade-dialog-content {
      border-radius: 4px;
    }
  }
}

/* High-density display optimization */
@media (-webkit-min-device-pixel-ratio: 2), (min-resolution: 2dppx) {
  .upgrade-membership-dialog {
    .membership-option-content {
      .el-tag {
        border-width: 0.5px;
      }
    }
  }
}
</style>
